import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Image,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTypingAnimation } from '../../hooks/useTypingAnimation';
import { formatExternalUrl } from '../../utils/formatExternalUrl';
import { RESOURCE_CATEGORIES } from '../../constants/resourceCategories';
import { MERCH_RESOURCE_CATEGORY } from '../../utils/resourceCategoryCatalog';
import { keepVisibleSelectedIds } from '../../utils/venueDerivedResources';
import { CommunityPostCard } from '../CommunityPostCard';
import { useCommunity } from '../../contexts/CommunityContext';
import { useCommunityModal } from '../../contexts/CommunityModalContext';
import { useUser } from '../LoginPersistence';
import { getFirestore, collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { resolveEmbedCoordinatesForSave, extractGooglePlaceRefFromMapEmbed, formatEmbedResolveStatus } from '../../utils/mapEmbedUtils';
import FullscreenSafeModal from '../FullscreenSafeModal';
import { useMapContributorGate } from './MapAccessPrompt';
import { FONT, LAYER_COLOR } from '../../styles/tourTheme';
import {
  DEFAULT_FARM_RADIUS_MILES,
  MIN_FARM_RADIUS_MILES,
  MAX_FARM_RADIUS_MILES,
  farmDayCardParts,
  foodHubMatchesRailTab,
  FOOD_GIVE_LINKS,
  isPointWithinRouteRadius,
  readHubCoords,
} from '../../utils/farmRouteSearch';

function useMapEmbedResolve({
  embed,
  name = '',
  city = '',
  state = '',
  onPreview,
  onClear,
}) {
  const [embedStatus, setEmbedStatus] = useState({ state: 'idle', text: '' });
  const [resolvedCoords, setResolvedCoords] = useState(null);
  const embedResolveTimer = useRef(null);

  useEffect(() => {
    const trimmed = String(embed || '').trim();
    if (embedResolveTimer.current) clearTimeout(embedResolveTimer.current);
    if (!trimmed) {
      setEmbedStatus({ state: 'idle', text: '' });
      setResolvedCoords(null);
      onClear?.();
      return undefined;
    }
    const placeRef = extractGooglePlaceRefFromMapEmbed(trimmed);
    setEmbedStatus({
      state: 'loading',
      text: placeRef.featureId || placeRef.placeId
        ? 'Looking up Google place coordinates…'
        : 'Resolving coordinates from this embed…',
    });
    embedResolveTimer.current = setTimeout(async () => {
      const resolved = await resolveEmbedCoordinatesForSave(trimmed, { city, state, name });
      const status = formatEmbedResolveStatus(resolved);
      setEmbedStatus(status);
      if (resolved.latitude != null && resolved.longitude != null) {
        const coords = {
          latitude: resolved.latitude,
          longitude: resolved.longitude,
        };
        setResolvedCoords(coords);
        onPreview?.({
          ...coords,
          name: name || placeRef.placeName || 'Preview location',
        });
      } else {
        setResolvedCoords(null);
        onClear?.();
      }
    }, 700);
    return () => {
      if (embedResolveTimer.current) clearTimeout(embedResolveTimer.current);
    };
  }, [embed, city, state, name, onPreview, onClear]);

  return { embedStatus, resolvedCoords };
}

function MapEmbedField({
  label = 'Google Maps link or embed (optional)',
  value,
  onChangeText,
  embedStatus,
  placeholder = 'Paste a Google Maps link or embed',
  styles,
  placeholderColor,
}) {
  return (
    <>
      <Text style={styles.sideModalFieldLabel}>{label}</Text>
      <TextInput
        style={styles.sideModalInput}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        autoCapitalize="none"
        placeholderTextColor={placeholderColor}
      />
      {embedStatus.text ? (
        <Text style={[
          styles.resourceFormHelpText,
          { marginTop: 6, marginBottom: 8 },
          embedStatus.state === 'loading' && { color: '#f39c12' },
          embedStatus.state === 'error' && { color: '#e74c3c' },
          embedStatus.state === 'ready' && { color: '#00A575' },
        ]}>
          {embedStatus.text}
        </Text>
      ) : null}
    </>
  );
}

const TRAFFIC_CONE_IMG = require('../../../assets/traffic-cone.png');

/** Full-height vertical icon rail (taskbar-style) for the Tour Planner map. */
export const MAP_SIDE_RAIL_WIDTH = 40;
/** Icon-only rail width on mobile (matches iconsColMobile). */
export const MAP_SIDE_RAIL_MOBILE_WIDTH = 36;
export const MAP_SIDE_LABELS_WIDTH = 136;
export const MAP_SIDE_PANEL_WIDTH = 340;
const THIN_SCROLLBAR_PROPS = Platform.OS === 'web'
  ? { className: 'vrc-venue-filters-scroll' }
  : {};
const RESOURCE_SEARCH_PLACEHOLDERS = [
  'Merch',
  'Accessibility',
  'Rehearsal',
  'Environment',
  'Safety',
  'Education',
  'Repair directory',
];
const FOOD_SEARCH_PLACEHOLDERS = [
  'Vegan',
  'Coffee',
  'Late night',
  'Grocery',
  'Catering',
  'Restaurants',
];
const EMPTY_PHOTO = { file: null, preview: '', width: null, height: null };

function selectPhotoFromFile(file, setPhoto) {
  const preview = URL.createObjectURL(file);
  setPhoto((prev) => {
    if (prev.preview) URL.revokeObjectURL(prev.preview);
    return { file, preview, width: null, height: null };
  });
  const probe = new window.Image();
  probe.onload = () => {
    setPhoto((prev) => (
      prev.preview === preview
        ? { ...prev, width: probe.naturalWidth, height: probe.naturalHeight }
        : prev
    ));
  };
  probe.src = preview;
}

function clearPhotoState(setPhoto) {
  setPhoto((prev) => {
    if (prev.preview) URL.revokeObjectURL(prev.preview);
    return { ...EMPTY_PHOTO };
  });
}

function CoverPhotoPicker({
  isDarkMode,
  styles,
  photo,
  onSelectFile,
  onClear,
  subjectLabel = 'resource',
}) {
  return (
    <>
      <Text style={styles.sideModalFieldLabel}>Cover photo (optional)</Text>
      <Text style={styles.resourceFormHelpText}>
        Add a banner or logo to show with this {subjectLabel}. Wide banners and square logos both
        work — we preserve the original aspect ratio.
      </Text>
      {photo.preview ? (
        <View style={styles.resourcePhotoPreviewWrap}>
          <View style={[
            styles.resourcePhotoPreview,
            { aspectRatio: photo.width && photo.height ? photo.width / photo.height : 2.5 },
          ]}>
            <Image
              source={{ uri: photo.preview }}
              style={styles.resourcePhotoPreviewImage}
              resizeMode="contain"
            />
          </View>
          <View style={styles.resourcePhotoActions}>
            {Platform.OS === 'web' ? (
              <label style={{ cursor: 'pointer' }}>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(event) => {
                    const file = event?.target?.files?.[0];
                    if (file) onSelectFile(file);
                    event.target.value = '';
                  }}
                />
                <Text style={styles.resourcePhotoChangeText}>Change photo</Text>
              </label>
            ) : null}
            <TouchableOpacity onPress={onClear}>
              <Text style={styles.resourcePhotoRemoveText}>Remove</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : Platform.OS === 'web' ? (
        <label style={{ display: 'block', cursor: 'pointer' }}>
          <input
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event?.target?.files?.[0];
              if (file) onSelectFile(file);
              event.target.value = '';
            }}
          />
          <View style={styles.resourcePhotoDropzone}>
            <Ionicons
              name="image-outline"
              size={32}
              color={isDarkMode ? 'rgba(255,255,255,0.52)' : 'rgba(0,0,0,0.46)'}
            />
            <Text style={styles.resourcePhotoDropzoneTitle}>Choose a photo</Text>
            <Text style={styles.resourcePhotoDropzoneHint}>PNG or JPG recommended</Text>
          </View>
        </label>
      ) : null}
    </>
  );
}

function TextUnderlineToggle({ styles, options, value, onChange }) {
  return (
    <View style={styles.textToggleRow}>
      {options.map((option) => {
        const active = value === option.id;
        return (
          <TouchableOpacity
            key={option.id}
            onPress={() => onChange(option.id)}
            activeOpacity={0.8}
            style={Platform.OS === 'web' ? { cursor: 'pointer' } : null}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
          >
            <Text style={[styles.textToggleLabel, active && styles.textToggleLabelActive]}>
              {option.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

function getDirectorySearchText(entity) {
  const parts = [
    entity.postContent,
    entity.displayName,
    entity.location,
    entity.username,
    entity.firstName,
    entity.lastName,
    entity.title,
    entity.bio,
    entity.listingType,
    entity.price,
  ];
  if (Array.isArray(entity.items)) {
    entity.items.forEach((item) => {
      if (item && (item.type === 'text' || item.type === 'link' || item.type === 'date') && item.value) {
        parts.push(String(item.value));
      }
    });
  }
  return parts.filter(Boolean).join(' ').toLowerCase();
}

function getPostPinCoordinates(post, venues = []) {
  const lat = Number(post?.latitude ?? post?.lat);
  const lng = Number(post?.longitude ?? post?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return { lat, lng };
  }

  const items = Array.isArray(post?.items) ? post.items : [];
  for (const item of items) {
    if (item?.type !== 'map' || !item.value) continue;
    try {
      const parsed = typeof item.value === 'string' ? JSON.parse(item.value) : item.value;
      const mapLat = Number(parsed?.lat);
      const mapLng = Number(parsed?.lng);
      if (Number.isFinite(mapLat) && Number.isFinite(mapLng)) {
        return { lat: mapLat, lng: mapLng };
      }
    } catch (_) {}
  }

  const loc = String(post?.location || '').trim().toLowerCase();
  if (!loc || !venues.length) return null;

  const withCoords = (venue) => {
    const venueLat = Number(venue.latitude ?? venue.lat);
    const venueLng = Number(venue.longitude ?? venue.lng);
    if (!Number.isFinite(venueLat) || !Number.isFinite(venueLng)) return null;
    return { lat: venueLat, lng: venueLng };
  };

  const exactName = venues.find((venue) => String(venue.name || '').trim().toLowerCase() === loc);
  if (exactName) return withCoords(exactName);

  const cityStateMatch = venues.find((venue) => {
    const cityState = [venue.city, venue.state].filter(Boolean).join(', ').trim().toLowerCase();
    return cityState && loc === cityState;
  });
  if (cityStateMatch) return withCoords(cityStateMatch);

  const nameContains = venues.find((venue) => {
    const name = String(venue.name || '').trim().toLowerCase();
    return name && (loc.includes(name) || name.includes(loc));
  });
  if (nameContains) return withCoords(nameContains);

  const cityMatch = venues.find((venue) => {
    const city = String(venue.city || '').trim().toLowerCase();
    return city && loc === city;
  });
  return cityMatch ? withCoords(cityMatch) : null;
}

const GEAR_LISTING_TYPE_COLORS = {
  SALE: '#0a59f0',
  TRADE: '#03bb8d',
  ISO: '#787a99',
};

export const MAP_SIDE_RAIL_ITEMS = [
  { id: 'map', icon: 'map-outline', label: 'Venue Criteria' },
  { id: 'leaf', icon: 'leaf-outline', label: 'Resources' },
  { id: 'plane', icon: 'airplane-outline', label: 'Travel' },
  { id: 'food', icon: 'restaurant-outline', label: 'Fridges and Farm Stops' },
  { id: 'directory', icon: 'albums-outline', label: 'Directory/Gear' },
  { id: 'person', icon: 'person-outline', label: 'Saved Contacts' },
  { id: 'school', icon: 'school-outline', label: 'Learn' },
  { id: 'poster', icon: 'newspaper-outline', label: 'Event Flyers' },
  { id: 'plus', icon: 'add', label: 'Add to Map' },
];

/** Same chip set as the map filter panel affiliate section. */
const AFFILIATE_CHIP_VALUES = [
  'Live Nation', 'AEG', 'Independent', 'Bar', 'Brewery', 'DIY', 'Event Space',
  'Nonprofit', 'Record Shop', 'Restaurant', 'Skate', 'Art Gallery', 'Coffee Shop',
  'Tea House', 'Other',
];

/** Same chip set as the map filter panel features section. */
const FEATURE_CHIP_OPTIONS = [
  { label: 'ADA info', value: 'hasADA' },
  { label: '100% Merch', value: 'merch100' },
  { label: 'Backstage', value: 'hasBackstage' },
  { label: 'Backline', value: 'hasBackline' },
  { label: 'Floor Stage', value: 'stageFloor' },
  { label: 'Parking', value: 'hasParking' },
  { label: 'Laundry', value: 'hasLaundry' },
  { label: 'Rehearsal', value: 'hasRehearsal' },
  { label: 'Pinball!', value: 'hasPinball' },
  { label: 'Shower', value: 'hasShower' },
  { label: 'Shore Power', value: 'hasShorePower' },
  { label: 'Spec Links', value: 'hasSpecLink' },
  { label: 'Video', value: 'hasVideo' }, // REQ-104: same state as the map's Video button
  { label: '🌱 Vegan food', value: 'hasVeganFood' },
  { label: '🌿 Lodging on-site', value: 'hasLodging' },
];

/**
 * Calendar MAP tab: sections can limit results to a radius around the selected day's route.
 * `routeRadius` = { lineCoords, radii: { [section]: miles }, onChangeRadius(section, miles) }, or null
 * outside the calendar (then nothing is filtered and no control renders).
 */
function useRouteRadiusFilter(routeRadius, section) {
  const lineCoords = routeRadius?.lineCoords;
  const miles = routeRadius?.radii?.[section];
  return useCallback((item) => {
    if (!lineCoords?.length || !miles) return true;
    const coords = readHubCoords(item);
    // Nothing to measure (e.g. city- or state-wide resources): keep it rather than hide it.
    if (!coords) return true;
    return isPointWithinRouteRadius(coords, lineCoords, miles);
  }, [lineCoords, miles]);
}

/** Normalize a list entry to a map preview pin ({ id, name, latitude, longitude }), or null. */
function toPreviewPin(id, name, source) {
  const coords = readHubCoords(source);
  return coords && id != null ? { id: String(id), name: name || '', ...coords } : null;
}

/**
 * Calendar MAP tab: publish the open section's current matches (while searching or browsing a
 * category) so TourPlanner can pin everything available along the route. Cleared on unmount.
 */
function useRoutePreviewPins(routeRadius, section, pins) {
  const publish = routeRadius?.onPreviewPins;
  const pinsRef = useRef(pins);
  pinsRef.current = pins;
  const key = pins.map((pin) => pin.id).join('|');
  useEffect(() => {
    if (!publish) return;
    publish(section, pinsRef.current);
  }, [publish, section, key]);
  useEffect(() => () => publish?.(section, []), [publish, section]);
}

function RouteRadiusControl({ styles, routeRadius, section, color = '#e67e22' }) {
  if (!routeRadius) return null;
  const miles = routeRadius.radii?.[section] ?? DEFAULT_FARM_RADIUS_MILES;
  const hasRoute = (routeRadius.lineCoords?.length || 0) > 0;
  return (
    <View style={styles.routeRadiusWrap}>
      <View style={styles.routeRadiusHeader}>
        <Text style={styles.resourceResultsTitle}>Search radius</Text>
        <Text style={[styles.routeRadiusValue, { color }]}>{miles} mi</Text>
      </View>
      {Platform.OS === 'web' ? (
        <input
          type="range"
          min={MIN_FARM_RADIUS_MILES}
          max={MAX_FARM_RADIUS_MILES}
          step={5}
          value={miles}
          onChange={(event) => routeRadius.onChangeRadius?.(section, Number(event.target.value))}
          style={{ width: '100%', accentColor: color }}
        />
      ) : null}
      <Text style={styles.routeRadiusHint}>
        {hasRoute
          ? `Within ${miles} mi of this day’s route`
          : 'Add stops to this day to search along its route.'}
      </Text>
    </View>
  );
}

function SectionSearchBar({ isDarkMode, styles, value, onChangeText, placeholder }) {
  return (
    <View style={styles.resourceSearchRow}>
      <View style={styles.resourceSearchBox}>
        <TextInput
          style={styles.resourceSearchInput}
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.42)'}
          clearButtonMode="while-editing"
          backgroundColor="transparent"
        />
        <Ionicons
          name="search"
          size={16}
          color={isDarkMode ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.42)'}
        />
      </View>
    </View>
  );
}

function VenueCriteriaPanel({
  isDarkMode,
  styles,
  filters,
  onToggleAffiliate,
  onToggleFeature,
  affiliateChipColor,
  onDeselectAll,
  searchQuery,
  onSearchChange,
  routeRadius = null,
}) {
  const [activeTab, setActiveTab] = useState('affiliate');
  const affiliateCount = filters?.affiliate?.length || 0;
  const featuresCount = filters?.features?.length || 0;
  const hasAny = affiliateCount > 0 || featuresCount > 0;

  const tabs = [
    { id: 'affiliate', label: 'Affiliate', count: affiliateCount },
    { id: 'features', label: 'Features', count: featuresCount },
  ];

  return (
    <View style={styles.criteriaRoot}>
      {onSearchChange ? (
        <SectionSearchBar
          isDarkMode={isDarkMode}
          styles={styles}
          value={searchQuery || ''}
          onChangeText={onSearchChange}
          placeholder="Search venues..."
        />
      ) : null}
      <View style={styles.criteriaTopRow}>
        <View style={styles.criteriaTabsRow}>
          {tabs.map((tab) => {
            const open = activeTab === tab.id;
            return (
              <TouchableOpacity
                key={tab.id}
                style={[
                  styles.categoryPill,
                  open && styles.categoryPillOpen,
                  tab.count > 0 && !open && styles.categoryPillHasFilter,
                ]}
                onPress={() => setActiveTab(tab.id)}
              >
                <Text style={[styles.categoryPillText, open && styles.categoryPillTextOpen]}>
                  {tab.label}
                </Text>
                {tab.count > 0 ? (
                  <Text style={styles.categoryPillBadge}>{tab.count}</Text>
                ) : null}
              </TouchableOpacity>
            );
          })}
        </View>
        <TouchableOpacity
          onPress={onDeselectAll}
          disabled={!hasAny}
          accessibilityLabel="Deselect all venue criteria"
        >
          <Text
            style={[
              styles.deselectLink,
              !hasAny && styles.deselectLinkDisabled,
            ]}
          >
            Deselect all
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        {...THIN_SCROLLBAR_PROPS}
        style={styles.criteriaScroll}
        contentContainerStyle={styles.criteriaScrollContent}
        showsVerticalScrollIndicator
      >
        {activeTab === 'affiliate' ? (
          <View>
            <View style={styles.venueFilterRow}>
              {AFFILIATE_CHIP_VALUES.map((val) => {
                const active = filters?.affiliate?.includes(val);
                const color = affiliateChipColor?.(val) || '#5a8feb';
                return (
                  <TouchableOpacity
                    key={val}
                    onPress={() => onToggleAffiliate?.(val)}
                    style={[
                      styles.venueFilterChip,
                      { flexDirection: 'row', alignItems: 'center', gap: 5 },
                      active && { borderColor: color, backgroundColor: `${color}26` },
                    ]}
                  >
                    <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: color }} />
                    <Text style={[styles.venueFilterChipText, active && { color, fontWeight: '700' }]}>
                      {val}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <Text style={styles.hintText}>
              AND filter: venue must match every selected affiliate. None checked = all shown.
            </Text>
          </View>
        ) : (
          <View>
            <View style={styles.venueFilterRow}>
              {FEATURE_CHIP_OPTIONS.map((opt) => {
                const active = filters?.features?.includes(opt.value);
                return (
                  <TouchableOpacity
                    key={opt.value}
                    style={[styles.venueFilterChip, active && styles.venueFilterChipActive]}
                    onPress={() => onToggleFeature?.(opt.value)}
                  >
                    <Text style={[styles.venueFilterChipText, active && styles.venueFilterChipTextActive]}>
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <Text style={styles.hintText}>
              AND filter: venue must have every selected feature.
            </Text>
          </View>
        )}
      </ScrollView>
      <RouteRadiusControl styles={styles} routeRadius={routeRadius} section="venues" />
    </View>
  );
}

function TravelPanel({
  isDarkMode,
  styles,
  pinModeCategories = {},
  activePoiCategories = [],
  poiMeta = {},
  onTogglePoiCategory,
  onDeselectPoiGroup,
  loadingPOIs = false,
  showBusStops,
  setShowBusStops,
  showTrainStops,
  setShowTrainStops,
  showAirports,
  setShowAirports,
  showBikeLanes,
  setShowBikeLanes,
  ecoRouteColors,
  setEcoRouteColors,
  bikeLanesLoading = false,
  onDeselectTransit,
  restrictToTourRoute = false,
  onToggleRouteOnly,
  onSubmitTravel,
  onEmbedLocationPreview,
  onClearEmbedLocationPreview,
  openRequest = null,
  onOpenRequestHandled = null,
  fullscreenHostRef = null,
  routeRadius = null,
  /** Calendar only: (text) => Promise<place[]> — live name search along the selected day's route. */
  onSearchPlaces = null,
  onSelectPlace = null,
}) {
  const requireContributor = useMapContributorGate();
  const [showTravelForm, setShowTravelForm] = useState(false);
  const [chipQuery, setChipQuery] = useState('');
  const placeSearchEnabled = Boolean(onSearchPlaces);
  // With place search on, the box searches places, so the category chips stay unfiltered.
  const normalizedChipQuery = placeSearchEnabled ? '' : chipQuery.trim().toLowerCase();
  const [placeResults, setPlaceResults] = useState([]);
  const [placeSearchState, setPlaceSearchState] = useState('idle'); // idle | loading | done | error
  const travelRadiusMiles = routeRadius?.radii?.travel;
  const routeLineCoords = routeRadius?.lineCoords;
  const hasRouteLine = (routeLineCoords?.length || 0) > 0;
  const placeQuery = chipQuery.trim();
  useRoutePreviewPins(
    routeRadius,
    'travel',
    placeSearchState === 'done'
      ? placeResults.map((place) => toPreviewPin(place.id, place.name, place)).filter(Boolean)
      : [],
  );

  useEffect(() => {
    if (!placeSearchEnabled) return undefined;
    if (placeQuery.length < 3 || !hasRouteLine) {
      setPlaceResults([]);
      setPlaceSearchState('idle');
      return undefined;
    }
    let cancelled = false;
    setPlaceSearchState('loading');
    const timer = setTimeout(async () => {
      try {
        const results = await onSearchPlaces(placeQuery);
        if (cancelled) return;
        setPlaceResults(Array.isArray(results) ? results : []);
        setPlaceSearchState('done');
      } catch (err) {
        console.warn('Travel place search failed', err);
        if (cancelled) return;
        setPlaceResults([]);
        setPlaceSearchState('error');
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [placeSearchEnabled, placeQuery, onSearchPlaces, travelRadiusMiles, routeLineCoords, hasRouteLine]);
  const chipMatches = (...labels) =>
    !normalizedChipQuery || labels.some((label) => String(label || '').toLowerCase().includes(normalizedChipQuery));
  const [travelForm, setTravelForm] = useState({
    locationType: 'wellness',
    name: '',
    address: '',
    website: '',
    mapEmbedLink: '',
    notes: '',
  });
  const [travelPhoto, setTravelPhoto] = useState(EMPTY_PHOTO);
  const travelPlaceholder = isDarkMode ? 'rgba(255,255,255,0.36)' : 'rgba(0,0,0,0.36)';
  const { embedStatus: travelEmbedStatus } = useMapEmbedResolve({
    embed: showTravelForm ? travelForm.mapEmbedLink : '',
    name: travelForm.name,
    onPreview: onEmbedLocationPreview,
    onClear: onClearEmbedLocationPreview,
  });

  useEffect(() => {
    if (openRequest?.action !== 'add') return;
    setShowTravelForm(true);
    onOpenRequestHandled?.();
  }, [openRequest, onOpenRequestHandled]);

  const resetTravelForm = () => {
    onClearEmbedLocationPreview?.();
    setTravelForm({
      locationType: 'wellness',
      name: '',
      address: '',
      website: '',
      mapEmbedLink: '',
      notes: '',
    });
    clearPhotoState(setTravelPhoto);
    setShowTravelForm(false);
  };

  const submitTravel = () => {
    if (!travelForm.name.trim()) return;
    onSubmitTravel?.({
      ...travelForm,
      name: travelForm.name.trim(),
      photoFile: travelPhoto.file,
      photoWidth: travelPhoto.width,
      photoHeight: travelPhoto.height,
    });
    resetTravelForm();
  };
  const poiSections = [
    { id: 'wellness', title: 'Health and wellness' },
    { id: 'parks', title: 'Parks and rec' },
    { id: 'travel', title: 'Travel + Lodging' },
  ];
  const transitOptions = [
    { label: 'Bus Stops', icon: 'bus-outline', active: showBusStops, setActive: setShowBusStops },
    { label: 'Train / Rail', icon: 'train-outline', active: showTrainStops, setActive: setShowTrainStops },
    { label: 'Airports', icon: 'airplane-outline', active: showAirports, setActive: setShowAirports },
    { label: 'Bike Lanes', icon: 'bicycle-outline', active: showBikeLanes, setActive: setShowBikeLanes },
  ];
  const transitHasActive =
    showBusStops || showTrainStops || showAirports || showBikeLanes || ecoRouteColors;

  const renderHeader = (title, hasActive, onDeselect) => (
    <View style={styles.travelSectionHeader}>
      <Text style={styles.travelSectionTitle}>{title}</Text>
      <TouchableOpacity
        onPress={onDeselect}
        disabled={!hasActive}
        accessibilityLabel={`Deselect all ${title}`}
      >
        <Text style={[styles.deselectLink, !hasActive && styles.deselectLinkDisabled]}>
          Deselect all
        </Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={{ flex: 1 }}>
    <ScrollView
      {...THIN_SCROLLBAR_PROPS}
      style={styles.travelRoot}
      contentContainerStyle={styles.travelContent}
      showsVerticalScrollIndicator
    >
      <SectionSearchBar
        isDarkMode={isDarkMode}
        styles={styles}
        value={chipQuery}
        onChangeText={setChipQuery}
        placeholder={placeSearchEnabled ? 'Search places along this day’s route...' : 'Search travel categories...'}
      />
      {placeSearchEnabled && placeQuery ? (
        <View style={styles.travelSection}>
          <Text style={styles.resourceResultsTitle}>
            {!hasRouteLine
              ? 'Add stops to this day to search along its route.'
              : placeQuery.length < 3
                ? 'Type at least 3 letters to search.'
                : placeSearchState === 'loading'
                  ? 'Searching this day’s route…'
                  : placeSearchState === 'error'
                    ? 'Search failed. Try again in a moment.'
                    : placeResults.length
                      ? `${placeResults.length} ${placeResults.length === 1 ? 'place' : 'places'} within ${travelRadiusMiles} mi`
                      : `No matching places within ${travelRadiusMiles} mi of this day’s route.`}
          </Text>
          {placeSearchState === 'loading' ? (
            <ActivityIndicator size="small" color="#e67e22" style={{ marginTop: 8 }} />
          ) : null}
          {placeSearchState === 'done' ? placeResults.map((place) => (
            <TouchableOpacity
              key={place.id}
              style={[styles.resourceResultItem, { marginTop: 6 }]}
              onPress={() => onSelectPlace?.(place)}
              accessibilityLabel={`Show ${place.name} on the map`}
            >
              <View style={styles.resourceResultCopy}>
                <Text style={styles.resourceResultName} numberOfLines={1}>
                  {place.icon} {place.name}
                </Text>
                <Text style={styles.resourceResultDescription} numberOfLines={1}>
                  {place.categoryLabel} · {Number(place.milesFromRoute).toFixed(1)} mi off route
                </Text>
              </View>
            </TouchableOpacity>
          )) : null}
        </View>
      ) : null}
      <TouchableOpacity
        style={[styles.resourceSubmitButton, { marginBottom: 12 }]}
        onPress={() => { if (requireContributor()) setShowTravelForm(true); }}
      >
        <Text style={styles.resourceSubmitButtonText}>ADD TO MAP</Text>
      </TouchableOpacity>

      {loadingPOIs ? (
        <View style={styles.travelLoadingRow}>
          <ActivityIndicator size="small" color="#e67e22" />
          <Text style={styles.travelLoadingText}>Loading map locations…</Text>
        </View>
      ) : null}

      {poiSections.map((section) => {
        const categories = pinModeCategories[section.id] || [];
        const hasActive = categories.some((type) => activePoiCategories.includes(type));
        const shownCategories = chipMatches(section.title)
          ? categories
          : categories.filter((type) => chipMatches(poiMeta[type]?.label, type));
        if (shownCategories.length === 0) return null;
        return (
          <View key={section.id} style={styles.travelSection}>
            {renderHeader(
              section.title,
              hasActive,
              () => onDeselectPoiGroup?.(categories),
            )}
            <View style={styles.travelChipRow}>
              {shownCategories.map((type) => {
                const meta = poiMeta[type] || {};
                const active = activePoiCategories.includes(type);
                const color = meta.color || '#e67e22';
                return (
                  <TouchableOpacity
                    key={type}
                    style={[
                      styles.travelChip,
                      active && { backgroundColor: color, borderColor: color },
                    ]}
                    onPress={() => onTogglePoiCategory?.(type)}
                    accessibilityState={{ selected: active }}
                  >
                    <Text style={[styles.travelChipText, active && styles.travelChipTextActive]}>
                      {meta.icon || '•'} {meta.label || type}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        );
      })}

      {(chipMatches('Transit', 'CO2 route colors', 'CO₂ route colors') || transitOptions.some((option) => chipMatches(option.label))) ? (
      <View style={styles.travelSection}>
        {renderHeader('Transit', transitHasActive, onDeselectTransit)}
        <View style={styles.travelChipRow}>
          {(chipMatches('Transit') ? transitOptions : transitOptions.filter((option) => chipMatches(option.label))).map((option) => (
            <TouchableOpacity
              key={option.label}
              onPress={() => option.setActive?.((value) => !value)}
              style={[styles.travelChip, option.active && styles.travelTransitChipActive]}
              accessibilityState={{ selected: Boolean(option.active) }}
            >
              <Ionicons
                name={option.icon}
                size={13}
                color={option.active
                  ? (isDarkMode ? '#062a38' : '#ffffff')
                  : (isDarkMode ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.62)')}
              />
              <Text
                style={[
                  styles.travelChipText,
                  option.active && styles.travelTransitChipTextActive,
                ]}
              >
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity
            onPress={() => setEcoRouteColors?.((value) => !value)}
            style={[
              styles.travelChip,
              ecoRouteColors && { backgroundColor: '#27ae60', borderColor: '#27ae60' },
            ]}
            accessibilityState={{ selected: Boolean(ecoRouteColors) }}
          >
            <Text style={styles.travelChipEmoji}>🌱</Text>
            <Text style={[styles.travelChipText, ecoRouteColors && styles.travelChipTextActive]}>
              CO₂ Route Colors
            </Text>
          </TouchableOpacity>
        </View>
        {showBikeLanes ? (
          <Text style={styles.hintText}>
            {bikeLanesLoading ? 'Loading bike lanes…' : '🟣 Available  🟢 Commonly used · zoom 11+'}
          </Text>
        ) : null}
      </View>
      ) : null}

      {/* In the calendar the search radius below replaces this route-only switch. */}
      {routeRadius ? null : (
      <TouchableOpacity
        onPress={() => onToggleRouteOnly?.()}
        style={[
          styles.travelChip,
          restrictToTourRoute && { backgroundColor: '#27ae60', borderColor: '#27ae60' },
          { alignSelf: 'flex-start', marginBottom: 12 },
        ]}
        accessibilityState={{ selected: Boolean(restrictToTourRoute) }}
      >
        <Text style={[styles.travelChipText, restrictToTourRoute && styles.travelChipTextActive]}>
          only show locations within tour route
        </Text>
      </TouchableOpacity>
      )}

      <View style={styles.travelLegend}>
        <Text style={styles.travelLegendTitle}>Legend</Text>
        <View style={styles.travelLegendItems}>
          {[
            { ionicon: 'bus', color: '#8B5CF6', label: 'Bus stop' },
            { ionicon: 'train', color: '#DC2626', label: 'Train/Amtrak' },
            { ionicon: 'airplane', color: '#EC4899', label: 'Airport' },
            { ionicon: 'bicycle', color: '#10B981', label: 'Bike lanes' },
            { image: TRAFFIC_CONE_IMG, label: 'Accessibility Flags' },
          ].map((item) => (
            <View key={item.label} style={styles.travelLegendItem}>
              {item.image ? (
                <Image source={item.image} style={styles.travelLegendImage} resizeMode="contain" />
              ) : (
                <Ionicons name={item.ionicon} size={13} color={item.color} />
              )}
              <Text style={styles.travelLegendText}>{item.label}</Text>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
      <RouteRadiusControl styles={styles} routeRadius={routeRadius} section="travel" />

      <FullscreenSafeModal
        visible={showTravelForm}
        onRequestClose={resetTravelForm}
        hostRef={fullscreenHostRef}
      >
        <View style={styles.sideModalBackdrop}>
          <View style={styles.sideModalCard}>
            <ScrollView
              {...THIN_SCROLLBAR_PROPS}
              contentContainerStyle={styles.resourceFormContent}
              keyboardShouldPersistTaps="handled"
            >
              <View style={styles.resourceFormHeader}>
                <Text style={styles.sideModalTitle}>ADD TO MAP</Text>
                <TouchableOpacity onPress={resetTravelForm} accessibilityLabel="Close form">
                  <Text style={styles.resourceFormClose}>✕</Text>
                </TouchableOpacity>
              </View>
              <Text style={styles.sideModalFieldLabel}>Travel Location Type (select)</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {[
                  { id: 'wellness', label: 'HEALTH + WELLNESS' },
                  { id: 'parks', label: 'PARKS + REC' },
                  { id: 'lodging', label: 'TRAVEL + LODGING' },
                  { id: 'transit', label: 'TRANSIT' },
                ].map((option) => (
                  <TouchableOpacity
                    key={option.id}
                    style={[
                      styles.travelChip,
                      travelForm.locationType === option.id && { backgroundColor: '#e67e22', borderColor: '#e67e22' },
                    ]}
                    onPress={() => setTravelForm((prev) => ({ ...prev, locationType: option.id }))}
                  >
                    <Text style={[
                      styles.travelChipText,
                      travelForm.locationType === option.id && styles.travelChipTextActive,
                    ]}>
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
              <Text style={styles.sideModalFieldLabel}>Travel Location Name *</Text>
              <TextInput
                style={styles.sideModalInput}
                value={travelForm.name}
                onChangeText={(name) => setTravelForm((prev) => ({ ...prev, name }))}
                placeholder="Name"
                placeholderTextColor={travelPlaceholder}
              />
              <Text style={styles.sideModalFieldLabel}>Travel Location Address</Text>
              <TextInput
                style={styles.sideModalInput}
                value={travelForm.address}
                onChangeText={(address) => setTravelForm((prev) => ({ ...prev, address }))}
                placeholder="Address"
                placeholderTextColor={travelPlaceholder}
              />
              <MapEmbedField
                styles={styles}
                placeholderColor={travelPlaceholder}
                value={travelForm.mapEmbedLink}
                onChangeText={(mapEmbedLink) => setTravelForm((prev) => ({ ...prev, mapEmbedLink }))}
                embedStatus={travelEmbedStatus}
              />
              <Text style={styles.sideModalFieldLabel}>Travel Location Website</Text>
              <TextInput
                style={styles.sideModalInput}
                value={travelForm.website}
                onChangeText={(website) => setTravelForm((prev) => ({ ...prev, website }))}
                placeholder="https://"
                autoCapitalize="none"
                placeholderTextColor={travelPlaceholder}
              />
              <CoverPhotoPicker
                isDarkMode={isDarkMode}
                styles={styles}
                photo={travelPhoto}
                onSelectFile={(file) => selectPhotoFromFile(file, setTravelPhoto)}
                onClear={() => clearPhotoState(setTravelPhoto)}
                subjectLabel="location"
              />
              <Text style={styles.sideModalFieldLabel}>Notes</Text>
              <TextInput
                style={[styles.sideModalInput, styles.sideModalTextArea]}
                value={travelForm.notes}
                onChangeText={(notes) => setTravelForm((prev) => ({ ...prev, notes }))}
                placeholder="Notes"
                placeholderTextColor={travelPlaceholder}
                multiline
              />
              <TouchableOpacity
                style={[styles.resourceSubmitButton, !travelForm.name.trim() && styles.sideModalButtonDisabled]}
                onPress={submitTravel}
                disabled={!travelForm.name.trim()}
              >
                <Text style={styles.resourceSubmitButtonText}>SEND</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </FullscreenSafeModal>
    </View>
  );
}

function ResourcesPanel({
  isDarkMode,
  styles,
  items = [],
  categories = [],
  selectedIds = [],
  loadingIds = [],
  venueOptions = [],
  onToggleItem,
  onDeselectAll,
  onKeepSelectedIds,
  onSubmitResource,
  onSubmitMerch,
  onEmbedLocationPreview,
  onClearEmbedLocationPreview,
  fullscreenHostRef = null,
  openRequest = null,
  onOpenRequestHandled = null,
  routeRadius = null,
}) {
  const requireContributor = useMapContributorGate();
  const [query, setQuery] = useState('');
  const [selectedBrowseCategory, setSelectedBrowseCategory] = useState(null);
  const inRouteRadius = useRouteRadiusFilter(routeRadius, 'resources');
  const [addStep, setAddStep] = useState(null);
  const [addCategory, setAddCategory] = useState(null);
  const [venueQuery, setVenueQuery] = useState('');
  const [venuePickerOpen, setVenuePickerOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '',
    url: '',
    description: '',
    imageUrl: '',
    scope: 'venue',
    venueId: '',
    venueName: '',
    city: '',
    state: '',
    latitude: '',
    longitude: '',
    services: '',
    notes: '',
    mapEmbedLink: '',
    tags: '',
  });
  const [photo, setPhoto] = useState(EMPTY_PHOTO);
  const isMerchForm = addCategory === 'merch';
  const { embedStatus, resolvedCoords } = useMapEmbedResolve({
    embed: addStep === 'form' ? addForm.mapEmbedLink : '',
    name: addForm.name,
    city: addForm.city,
    state: addForm.state,
    onPreview: onEmbedLocationPreview,
    onClear: onClearEmbedLocationPreview,
  });

  // Keep lat/lng fields in sync when embed resolves (merch form + any resource with those fields).
  useEffect(() => {
    if (!resolvedCoords) return;
    setAddForm((prev) => {
      const nextLat = String(resolvedCoords.latitude);
      const nextLng = String(resolvedCoords.longitude);
      if (prev.latitude === nextLat && prev.longitude === nextLng) return prev;
      return { ...prev, latitude: nextLat, longitude: nextLng };
    });
  }, [resolvedCoords]);
  const animatedPlaceholder = useTypingAnimation(
    RESOURCE_SEARCH_PLACEHOLDERS,
    80,
    30,
    1300,
  );
  const normalizedQuery = query.trim().toLowerCase();
  const selectedSet = new Set(selectedIds);
  const loadingSet = new Set(loadingIds);
  const browseCategories = [...categories];
  const knownCategoryIds = new Set(browseCategories.map((category) => category.id));
  [MERCH_RESOURCE_CATEGORY, ...items
    .filter((item) => item.categoryId)
    .map((item) => ({ id: item.categoryId, label: item.categoryLabel || item.categoryId }))].forEach((category) => {
    if (!knownCategoryIds.has(category.id)) {
      knownCategoryIds.add(category.id);
      browseCategories.push(category);
    }
  });
  const itemsInRadius = items.filter(inRouteRadius);
  const filteredItems = normalizedQuery
    ? itemsInRadius.filter((item) => item.categoryId === selectedBrowseCategory && item.searchText?.includes(normalizedQuery))
    : itemsInRadius.filter((item) => item.categoryId === selectedBrowseCategory);
  const queryLabel = query.trim() || 'Resource';
  useRoutePreviewPins(
    routeRadius,
    'resources',
    selectedBrowseCategory
      ? filteredItems.map((item) => toPreviewPin(item.id, item.name, item)).filter(Boolean)
      : [],
  );
  const selectedFormCategory = [...RESOURCE_CATEGORIES, MERCH_RESOURCE_CATEGORY, ...categories].find(
    (category) => category.id === addCategory
  ) || null;
  const placeholderColor = isDarkMode ? 'rgba(255,255,255,0.36)' : 'rgba(0,0,0,0.36)';
  const selectedAddVenue = venueOptions.find(
    (venue) => String(venue.id) === String(addForm.venueId)
  ) || null;
  const canSubmitAddForm = Boolean(
    addForm.name.trim() && (isMerchForm || addForm.venueId)
  );
  const venueMatches = venueQuery.trim()
    ? venueOptions.filter((venue) => [
      venue.name,
      venue.city,
      venue.state,
    ].filter(Boolean).join(' ').toLowerCase().includes(venueQuery.trim().toLowerCase())).slice(0, 8)
    : venueOptions.slice(0, 8);
  const addCategories = [...RESOURCE_CATEGORIES, MERCH_RESOURCE_CATEGORY, ...categories]
    .filter((category, index, all) => all.findIndex((item) => item.id === category.id) === index);

  useEffect(() => {
    if (!isMerchForm || !resolvedCoords) return;
    setAddForm((prev) => ({
      ...prev,
      latitude: String(resolvedCoords.latitude),
      longitude: String(resolvedCoords.longitude),
    }));
  }, [isMerchForm, resolvedCoords]);

  useEffect(() => {
    if (!openRequest) return;
    if (openRequest.action === 'addMerch') {
      setAddCategory('merch');
      setAddStep('form');
    } else if (openRequest.action === 'add') {
      setAddCategory(null);
      setAddStep('category');
    }
    onOpenRequestHandled?.();
  }, [openRequest, onOpenRequestHandled]);

  const closeAddFlow = () => {
    onClearEmbedLocationPreview?.();
    setAddStep(null);
    setAddCategory(null);
    setVenueQuery('');
    setVenuePickerOpen(false);
    setAddForm({
      name: '',
      url: '',
      description: '',
      imageUrl: '',
      scope: 'venue',
      venueId: '',
      venueName: '',
      city: '',
      state: '',
      latitude: '',
      longitude: '',
      services: '',
      notes: '',
      mapEmbedLink: '',
      tags: '',
    });
    clearPhotoState(setPhoto);
  };

  const chooseAddCategory = (categoryId) => {
    setAddCategory(categoryId);
    setAddStep('form');
  };

  const submitAddForm = () => {
    if (!canSubmitAddForm || !addCategory) return;
    const payload = {
      ...addForm,
      photoFile: photo.file,
      photoWidth: photo.width,
      photoHeight: photo.height,
      name: addForm.name.trim(),
      url: addForm.url.trim(),
      description: addForm.description.trim(),
      imageUrl: addForm.imageUrl.trim(),
      venueName: addForm.venueName.trim(),
      city: addForm.city.trim(),
      state: addForm.state.trim(),
      latitude: addForm.latitude.trim(),
      longitude: addForm.longitude.trim(),
      services: addForm.services.trim(),
      notes: addForm.notes.trim(),
      mapEmbedLink: addForm.mapEmbedLink.trim(),
    };
    if (addCategory === 'merch') {
      onSubmitMerch?.(payload);
    } else {
      onSubmitResource?.({ ...payload, category: addCategory });
    }
    closeAddFlow();
  };

  const selectResourceVenue = (venue) => {
    setAddForm((prev) => ({
      ...prev,
      venueId: String(venue.id),
      venueName: venue.name || '',
      city: venue.city || '',
      state: venue.state || '',
      scope: 'venue',
    }));
    setVenueQuery(venue.name || '');
    setVenuePickerOpen(false);
  };

  // Drop map selections that no longer appear in the current search results.
  useEffect(() => {
    if (!selectedBrowseCategory || !onKeepSelectedIds || selectedIds.length === 0) return;
    const visibleIds = new Set(filteredItems.map((item) => item.id));
    const keptIds = keepVisibleSelectedIds(selectedIds, items, visibleIds, selectedBrowseCategory);
    if (keptIds.length === selectedIds.length) return;
    onKeepSelectedIds(keptIds);
  }, [filteredItems, items, selectedIds, onKeepSelectedIds, selectedBrowseCategory]);
  return (
    <View style={styles.resourcesRoot}>
      {selectedBrowseCategory ? (
        <>
          <View style={styles.resourceSearchRow}>
            <TouchableOpacity
              onPress={() => {
                setSelectedBrowseCategory(null);
                setQuery('');
              }}
              accessibilityRole="button"
              accessibilityLabel="Choose another resource category"
              style={{ padding: 8 }}
            >
              <Ionicons name="arrow-back" size={18} color={isDarkMode ? '#fff' : '#333'} />
            </TouchableOpacity>
            <View style={styles.resourceSearchBox}>
              <TextInput
                style={styles.resourceSearchInput}
                value={query}
                onChangeText={setQuery}
                placeholder={animatedPlaceholder}
                placeholderTextColor={isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.42)'}
                clearButtonMode="while-editing"
                backgroundColor="transparent"
              />
              <Ionicons
                name="search"
                size={16}
                color={isDarkMode ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.42)'}
              />
            </View>
            <TouchableOpacity
              style={styles.resourceAddButton}
              onPress={() => { if (requireContributor()) setAddStep('category'); }}
              accessibilityLabel="Add a resource location"
            >
              <Ionicons name="add" size={20} color="#ffffff" />
            </TouchableOpacity>
          </View>

          <View style={styles.resourceResultsHeader}>
            <Text style={styles.resourceResultsTitle} numberOfLines={1}>
              {browseCategories.find((category) => category.id === selectedBrowseCategory)?.label || queryLabel} locations
            </Text>
            <TouchableOpacity
              onPress={onDeselectAll}
              disabled={selectedIds.length === 0}
              accessibilityLabel="Deselect all resource locations"
            >
              <Text
                style={[
                  styles.deselectLink,
                  selectedIds.length === 0 && styles.deselectLinkDisabled,
                ]}
              >
                Deselect all
              </Text>
            </TouchableOpacity>
          </View>

          <ScrollView
            {...THIN_SCROLLBAR_PROPS}
            style={styles.resourceResultsScroll}
            contentContainerStyle={styles.resourceResultsContent}
            showsVerticalScrollIndicator
          >
            {filteredItems.length === 0 ? (
              <Text style={styles.resourceEmptyText}>
                No matching locations or resources.
              </Text>
            ) : (
              filteredItems.map((item) => {
            const selected = selectedSet.has(item.id);
            const loading = loadingSet.has(item.id);
            const websiteUrl = formatExternalUrl(item.websiteUrl || item.url);
            const openWebsite = (e) => {
              e?.stopPropagation?.();
              if (!websiteUrl || typeof window === 'undefined') return;
              window.open(websiteUrl, '_blank', 'noopener,noreferrer');
            };
            return (
              <TouchableOpacity
                key={item.id}
                style={[
                  styles.resourceResultItem,
                  (selected || loading) && styles.resourceResultItemSelected,
                ]}
                onPress={() => onToggleItem?.(item)}
                accessibilityLabel={
                  loading
                    ? `Loading ${item.name} on map`
                    : `${selected ? 'Hide' : 'Show'} ${item.name} on map`
                }
                accessibilityState={{ selected: selected || loading, busy: loading }}
                activeOpacity={0.78}
              >
                <TouchableOpacity
                  style={[styles.resourceThumb, websiteUrl && styles.resourceThumbLink]}
                  onPress={openWebsite}
                  disabled={!websiteUrl}
                  accessibilityLabel={websiteUrl ? `Open ${item.name} website` : undefined}
                  accessibilityRole={websiteUrl ? 'link' : undefined}
                  activeOpacity={websiteUrl ? 0.75 : 1}
                >
                  {typeof item.imageUrl === 'string' && item.imageUrl ? (
                    <Image
                      source={{ uri: item.imageUrl }}
                      style={styles.resourceThumbImage}
                      resizeMode="cover"
                    />
                  ) : (
                    <Ionicons
                      name={item.type === 'merch' ? 'shirt-outline' : 'location-outline'}
                      size={20}
                      color={isDarkMode ? 'rgba(255,255,255,0.54)' : 'rgba(0,0,0,0.42)'}
                    />
                  )}
                </TouchableOpacity>
                <View style={styles.resourceResultCopy}>
                  <Text style={styles.resourceResultName} numberOfLines={1}>
                    {item.name}
                  </Text>
                  <Text style={styles.resourceResultDescription} numberOfLines={2}>
                    {item.description || item.location || 'Community resource'}
                  </Text>
                </View>
                <View
                  style={[
                    styles.resourceSelectMark,
                    (selected || loading) && styles.resourceSelectMarkSelected,
                  ]}
                >
                  {loading ? (
                    <ActivityIndicator
                      size="small"
                      color="#ffffff"
                      style={styles.resourceSelectSpinner}
                    />
                  ) : selected ? (
                    <Ionicons name="checkmark" size={14} color="#ffffff" />
                  ) : null}
                </View>
              </TouchableOpacity>
            );
              })
            )}
          </ScrollView>
        </>
      ) : (
        <ScrollView
          {...THIN_SCROLLBAR_PROPS}
          style={styles.resourceResultsScroll}
          contentContainerStyle={[styles.resourceResultsContent, { paddingTop: 10 }]}
          showsVerticalScrollIndicator
        >
          <View style={styles.resourceSearchRow}>
            <Text style={[styles.resourceResultsTitle, { flex: 1 }]}>Choose a resource category</Text>
            <TouchableOpacity
              style={styles.resourceAddButton}
              onPress={() => { if (requireContributor()) setAddStep('category'); }}
              accessibilityLabel="Add a resource location"
            >
              <Ionicons name="add" size={20} color="#ffffff" />
            </TouchableOpacity>
          </View>
          <View style={styles.resourceCategoryPicker}>
            {browseCategories.map((category) => {
              const count = itemsInRadius.filter((item) => item.categoryId === category.id).length;
              return (
                <TouchableOpacity
                  key={category.id}
                  style={styles.resourceCategoryOption}
                  onPress={() => {
                    setSelectedBrowseCategory(category.id);
                    setQuery('');
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={`Browse ${category.label} resources`}
                >
                  {category.source ? (
                    <Image
                      source={category.source}
                      style={[
                        styles.resourceCategoryImage,
                        category.invertInLightMode && !isDarkMode && Platform.OS === 'web'
                          ? { filter: 'invert(1)' }
                          : null,
                      ]}
                      resizeMode="contain"
                    />
                  ) : (
                    <Ionicons
                      name={category.iconName || (category.id === 'merch' ? 'shirt-outline' : 'construct-outline')}
                      size={24}
                      color="#e67e22"
                    />
                  )}
                  <Text style={styles.resourceCategoryOptionText}>{category.label}</Text>
                  <Text style={styles.resourceCategoryCount}>{count}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </ScrollView>
      )}
      <RouteRadiusControl styles={styles} routeRadius={routeRadius} section="resources" />

      <FullscreenSafeModal
        visible={addStep !== null}
        onRequestClose={closeAddFlow}
        hostRef={fullscreenHostRef}
      >
          <View style={styles.sideModalBackdrop}>
          <View style={styles.sideModalCard}>
            {addStep === 'category' ? (
              <>
                <Text style={styles.sideModalTitle}>What type of resource are you adding?</Text>
                <Text style={styles.sideModalSubtitle}>
                  Submissions appear after approval.
                </Text>
                <View style={styles.resourceCategoryPicker}>
                  {addCategories.map((category) => (
                    <TouchableOpacity
                      key={category.id}
                      style={styles.resourceCategoryOption}
                      onPress={() => chooseAddCategory(category.id)}
                    >
                      {category.source ? (
                        <Image
                          source={category.source}
                          style={[
                            styles.resourceCategoryImage,
                            category.invertInLightMode && !isDarkMode && Platform.OS === 'web'
                              ? { filter: 'invert(1)' }
                              : null,
                          ]}
                          resizeMode="contain"
                        />
                      ) : (
                        <Ionicons
                          name={category.iconName || (category.id === 'merch' ? 'shirt-outline' : 'construct-outline')}
                          size={22}
                          color="#e67e22"
                        />
                      )}
                      <Text style={styles.resourceCategoryOptionText}>{category.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
                <View style={styles.sideModalActions}>
                  <TouchableOpacity style={styles.sideModalNoButton} onPress={closeAddFlow}>
                    <Text style={styles.sideModalNoText}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              </>
            ) : (
              <ScrollView
                {...THIN_SCROLLBAR_PROPS}
                contentContainerStyle={styles.resourceFormContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
              >
                <View style={styles.resourceFormHeader}>
                  <Text style={styles.sideModalTitle}>
                    {isMerchForm ? 'Share a Merch Shop' : 'Share a Resource'}
                  </Text>
                  <TouchableOpacity onPress={closeAddFlow} accessibilityLabel="Close form">
                    <Text style={styles.resourceFormClose}>✕</Text>
                  </TouchableOpacity>
                </View>

                <View style={styles.resourceFormCategoryBanner}>
                  {selectedFormCategory?.source ? (
                    <Image
                      source={selectedFormCategory.source}
                      style={[
                        styles.resourceFormCategoryIcon,
                        selectedFormCategory.invertInLightMode && !isDarkMode && Platform.OS === 'web'
                          ? { filter: 'invert(1)' }
                          : null,
                      ]}
                      resizeMode="contain"
                    />
                  ) : (
                    <Ionicons name="shirt-outline" size={26} color="#e67e22" />
                  )}
                  <View>
                    <Text style={styles.resourceFormCategoryLabel}>Category</Text>
                    <Text style={styles.resourceFormCategoryValue}>
                      {isMerchForm ? 'Merch' : (selectedFormCategory?.label || '')}
                    </Text>
                  </View>
                </View>

                <Text style={styles.resourceFormHelpText}>
                  {isMerchForm
                    ? 'Suggest a merch shop for the map. Admins will review before it appears.'
                    : selectedAddVenue
                      ? `Suggest a resource for ${selectedAddVenue.name}${selectedAddVenue.city ? `, all venues in ${selectedAddVenue.city}` : ''}${selectedAddVenue.state ? `${selectedAddVenue.city ? ',' : ''} or all venues in ${selectedAddVenue.state}` : ''}. Admins will review before it appears.`
                      : 'Pick the venue this resource connects to. Admins will review before it appears.'}
                </Text>

                {!isMerchForm ? (
                  <View style={styles.resourceVenuePickerWrap}>
                    <Text style={styles.sideModalFieldLabel}>Venue *</Text>
                    <View style={[
                      styles.resourceVenuePicker,
                      selectedAddVenue && styles.resourceVenuePickerSelected,
                    ]}>
                      <TextInput
                        style={styles.resourceVenuePickerInput}
                        value={venueQuery}
                        onChangeText={(text) => {
                          setVenueQuery(text);
                          setVenuePickerOpen(true);
                          if (selectedAddVenue && text !== selectedAddVenue.name) {
                            setAddForm((prev) => ({
                              ...prev,
                              venueId: '',
                              venueName: '',
                              city: '',
                              state: '',
                            }));
                          }
                        }}
                        onFocus={() => setVenuePickerOpen(true)}
                        placeholder="Search venues by name, city, or state"
                        placeholderTextColor={placeholderColor}
                      />
                      <Ionicons
                        name={selectedAddVenue ? 'checkmark-circle' : 'search'}
                        size={17}
                        color={selectedAddVenue ? '#00A575' : '#e67e22'}
                      />
                    </View>
                    {venuePickerOpen ? (
                      <ScrollView
                        {...THIN_SCROLLBAR_PROPS}
                        style={styles.resourceVenueResults}
                        nestedScrollEnabled
                        keyboardShouldPersistTaps="handled"
                      >
                        {venueMatches.length > 0 ? venueMatches.map((venue) => (
                          <TouchableOpacity
                            key={venue.id}
                            style={styles.resourceVenueResult}
                            onPress={() => selectResourceVenue(venue)}
                          >
                            <Text style={styles.resourceVenueResultName} numberOfLines={1}>
                              {venue.name}
                            </Text>
                            <Text style={styles.resourceVenueResultLocation} numberOfLines={1}>
                              {[venue.city, venue.state].filter(Boolean).join(', ')}
                            </Text>
                          </TouchableOpacity>
                        )) : (
                          <Text style={styles.resourceVenueEmpty}>No matching venues.</Text>
                        )}
                      </ScrollView>
                    ) : null}
                  </View>
                ) : null}

                <Text style={styles.sideModalFieldLabel}>
                  {isMerchForm ? 'Shop name *' : 'Resource name *'}
                </Text>
                <TextInput
                  style={styles.sideModalInput}
                  value={addForm.name}
                  onChangeText={(name) => setAddForm((prev) => ({ ...prev, name }))}
                  placeholder={isMerchForm ? 'e.g. Eastside Screenprinting' : 'e.g. Local backline rental'}
                  placeholderTextColor={placeholderColor}
                />

                <MapEmbedField
                  styles={styles}
                  placeholderColor={placeholderColor}
                  value={addForm.mapEmbedLink}
                  onChangeText={(mapEmbedLink) => setAddForm((prev) => ({ ...prev, mapEmbedLink }))}
                  embedStatus={embedStatus}
                />

                <Text style={styles.sideModalFieldLabel}>URL (optional)</Text>
                <TextInput
                  style={styles.sideModalInput}
                  value={addForm.url}
                  onChangeText={(url) => setAddForm((prev) => ({ ...prev, url }))}
                  placeholder="https://"
                  autoCapitalize="none"
                  placeholderTextColor={placeholderColor}
                />

                {isMerchForm ? (
                  <>
                    <Text style={styles.sideModalFieldLabel}>Address (optional)</Text>
                    <TextInput
                      style={styles.sideModalInput}
                      value={addForm.venueName}
                      onChangeText={(venueName) => setAddForm((prev) => ({ ...prev, venueName }))}
                      placeholder="Street address"
                      placeholderTextColor={placeholderColor}
                    />
                    <View style={styles.sideModalInlineFields}>
                      <View style={styles.sideModalInlineField}>
                        <Text style={styles.sideModalFieldLabel}>City</Text>
                        <TextInput
                          style={styles.sideModalInput}
                          value={addForm.city}
                          onChangeText={(city) => setAddForm((prev) => ({ ...prev, city }))}
                          placeholder="City"
                          placeholderTextColor={placeholderColor}
                        />
                      </View>
                      <View style={styles.sideModalInlineField}>
                        <Text style={styles.sideModalFieldLabel}>State</Text>
                        <TextInput
                          style={styles.sideModalInput}
                          value={addForm.state}
                          onChangeText={(state) => setAddForm((prev) => ({ ...prev, state }))}
                          placeholder="State"
                          placeholderTextColor={placeholderColor}
                        />
                      </View>
                    </View>
                    <View style={styles.sideModalInlineFields}>
                      <View style={styles.sideModalInlineField}>
                        <Text style={styles.sideModalFieldLabel}>Latitude</Text>
                        <TextInput
                          style={styles.sideModalInput}
                          value={addForm.latitude}
                          onChangeText={(latitude) => setAddForm((prev) => ({ ...prev, latitude }))}
                          placeholder="e.g. 30.2672"
                          keyboardType="numeric"
                          placeholderTextColor={placeholderColor}
                        />
                      </View>
                      <View style={styles.sideModalInlineField}>
                        <Text style={styles.sideModalFieldLabel}>Longitude</Text>
                        <TextInput
                          style={styles.sideModalInput}
                          value={addForm.longitude}
                          onChangeText={(longitude) => setAddForm((prev) => ({ ...prev, longitude }))}
                          placeholder="e.g. -97.7431"
                          keyboardType="numeric"
                          placeholderTextColor={placeholderColor}
                        />
                      </View>
                    </View>
                    <Text style={styles.sideModalFieldLabel}>Services (optional)</Text>
                    <TextInput
                      style={[styles.sideModalInput, styles.sideModalTextArea]}
                      value={addForm.services}
                      onChangeText={(services) => setAddForm((prev) => ({ ...prev, services }))}
                      placeholder="Screenprinting, embroidery, fulfillment…"
                      placeholderTextColor={placeholderColor}
                      multiline
                    />
                    <Text style={styles.sideModalFieldLabel}>Notes (optional)</Text>
                    <TextInput
                      style={[styles.sideModalInput, styles.sideModalTextArea]}
                      value={addForm.notes}
                      onChangeText={(notes) => setAddForm((prev) => ({ ...prev, notes }))}
                      placeholder="Turnaround times, minimums, contacts…"
                      placeholderTextColor={placeholderColor}
                      multiline
                    />
                  </>
                ) : (
                  <>
                    <Text style={styles.sideModalFieldLabel}>Description (optional)</Text>
                    <TextInput
                      style={[styles.sideModalInput, styles.sideModalTextArea]}
                      value={addForm.description}
                      onChangeText={(description) => setAddForm((prev) => ({ ...prev, description }))}
                      placeholder="What is this resource and why is it useful?"
                      placeholderTextColor={placeholderColor}
                      multiline
                    />

                    <Text style={styles.sideModalFieldLabel}>Tags (optional)</Text>
                    <TextInput
                      style={styles.sideModalInput}
                      value={addForm.tags}
                      onChangeText={(tags) => setAddForm((prev) => ({ ...prev, tags }))}
                      placeholder="e.g. backline, all ages (separate with commas)"
                      autoCapitalize="none"
                      placeholderTextColor={placeholderColor}
                    />
                  </>
                )}

                <CoverPhotoPicker
                  isDarkMode={isDarkMode}
                  styles={styles}
                  photo={photo}
                  onSelectFile={(file) => selectPhotoFromFile(file, setPhoto)}
                  onClear={() => clearPhotoState(setPhoto)}
                  subjectLabel={isMerchForm ? 'shop' : 'resource'}
                />

                {!isMerchForm ? (
                  <>
                    <Text style={styles.sideModalFieldLabel}>Share with</Text>
                    <View style={styles.resourceScopeRow}>
                      {[
                        {
                          id: 'venue',
                          label: selectedAddVenue ? selectedAddVenue.name : 'This venue',
                          disabled: !selectedAddVenue,
                        },
                        {
                          id: 'city',
                          label: selectedAddVenue?.city
                            ? `All ${selectedAddVenue.city} venues`
                            : 'All city venues',
                          disabled: !selectedAddVenue?.city,
                        },
                        {
                          id: 'state',
                          label: selectedAddVenue?.state
                            ? `All ${selectedAddVenue.state} venues`
                            : 'All state venues',
                          disabled: !selectedAddVenue?.state,
                        },
                      ].map((scope) => (
                        <TouchableOpacity
                          key={scope.id}
                          style={[
                            styles.resourceScopeOption,
                            addForm.scope === scope.id && styles.resourceScopeOptionActive,
                            scope.disabled && styles.sideModalButtonDisabled,
                          ]}
                          onPress={() => setAddForm((prev) => ({ ...prev, scope: scope.id }))}
                          disabled={scope.disabled}
                        >
                          <Text style={[
                            styles.resourceScopeOptionText,
                            addForm.scope === scope.id && styles.resourceScopeOptionTextActive,
                          ]} numberOfLines={2}>
                            {scope.label}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </>
                ) : null}

                <TouchableOpacity
                  style={[
                    styles.resourceSubmitButton,
                    !canSubmitAddForm && styles.sideModalButtonDisabled,
                  ]}
                  onPress={submitAddForm}
                  disabled={!canSubmitAddForm}
                >
                  <Text style={styles.resourceSubmitButtonText}>SUBMIT FOR REVIEW</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setAddStep('category')}>
                  <Text style={styles.resourceFormBackLink}>Back to categories</Text>
                </TouchableOpacity>
              </ScrollView>
            )}
          </View>
          </View>
      </FullscreenSafeModal>
    </View>
  );
}

// Food hub descriptors — single source for label, icon and colour so the panel
// row, the tab and the map pin can never drift apart.
export const FOOD_HUB = {
  // Icons encode the DIRECTION of the transfer, not the kind of food. A farm is
  // where goods come off the truck's manifest; a food bank is where they go on.
  // Generic food icons (restaurant, nutrition) read as "place to eat" and lose
  // the pickup/drop-off distinction that is the whole point of the split.
  pickup:  { key: 'pickup',  label: 'Farms (pickup)',        icon: 'arrow-up-circle',   color: LAYER_COLOR.farm },
  dropoff: { key: 'dropoff', label: 'Fridges / Pantry',      icon: 'arrow-down-circle', color: LAYER_COLOR.foodBank },
};

function formatFarmDayChip(day) {
  const parts = farmDayCardParts(day);
  if (!parts.num) return parts.name;
  return `${parts.name} · ${parts.month} ${parts.num}`;
}

function FoodPanel({
  isDarkMode,
  styles,
  items = [],
  onSubmitFood,
  onAssignFood,
  onEmbedLocationPreview,
  onClearEmbedLocationPreview,
  fullscreenHostRef = null,
  openRequest = null,
  onOpenRequestHandled = null,
  corridorRadiusMiles = 25,
  onCorridorRadiusChange = null,
  corridorActive = false,
  corridorLoading = false,
  corridorMessage = '',
  routeDays = [],
  selectedDayId = null,
  selectedLegId = 'all',
  onSelectDay = null,
  onSelectLeg = null,
  onFoodModeChange = null,
  /** Calendar MAP tab: the day comes from the calendar, so only the day's route choice is shown. */
  hideDayPicker = false,
}) {
  const requireContributor = useMapContributorGate();
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState('pickup');
  const [selectedIds, setSelectedIds] = useState([]);
  const [assignmentItem, setAssignmentItem] = useState(null);
  const [assignRoute, setAssignRoute] = useState(true);
  const [assignRider, setAssignRider] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const onFoodModeChangeRef = useRef(onFoodModeChange);
  onFoodModeChangeRef.current = onFoodModeChange;
  useEffect(() => {
    onFoodModeChangeRef.current?.(activeTab);
  }, [activeTab]);

  // Opened programmatically from the "Add to Map" chooser.
  useEffect(() => {
    if (openRequest?.action !== 'add') return;
    setShowAddForm(true);
    onOpenRequestHandled?.();
  }, [openRequest, onOpenRequestHandled]);

  const [foodForm, setFoodForm] = useState({
    name: '',
    locationType: 'farm',
    networkName: '',
    address: '',
    website: '',
    mapEmbedUrl: '',
    hours: '',
    donationRules: '',
    description: '',
    pickup: true,
    dropoff: false,
  });
  const [photo, setPhoto] = useState(EMPTY_PHOTO);
  const { embedStatus } = useMapEmbedResolve({
    embed: showAddForm ? foodForm.mapEmbedUrl : '',
    name: foodForm.name,
    onPreview: onEmbedLocationPreview,
    onClear: onClearEmbedLocationPreview,
  });
  const animatedPlaceholder = useTypingAnimation(
    FOOD_SEARCH_PLACEHOLDERS,
    80,
    30,
    1300,
  );
  const placeholderColor = isDarkMode ? 'rgba(255,255,255,0.36)' : 'rgba(0,0,0,0.36)';
  const canSubmitFood = Boolean(
    foodForm.name.trim() && (foodForm.pickup || foodForm.dropoff)
  );
  const queryLabel = query.trim() || 'Food';
  const normalizedQuery = query.trim().toLowerCase();
  const visibleItems = items.filter((item) => {
    const supportsTab = foodHubMatchesRailTab(item, activeTab);
    if (!supportsTab) return false;
    if (!normalizedQuery) return true;
    return String(item.searchText || [
      item.name,
      item.description,
      item.location,
    ].filter(Boolean).join(' ')).toLowerCase().includes(normalizedQuery);
  });
  const selectedSet = new Set(selectedIds);

  const selectTab = (tab) => {
    setActiveTab(tab);
    setSelectedIds(
      items
        .filter((item) => foodHubMatchesRailTab(item, tab))
        .map((item) => item.id)
    );
  };

  const closeAssignment = () => {
    setAssignmentItem(null);
    setAssignRoute(true);
    setAssignRider(true);
  };

  const submitAssignment = () => {
    if (!assignmentItem || (!assignRoute && !assignRider)) return;
    onAssignFood?.(assignmentItem, { route: assignRoute, rider: assignRider, role: activeTab });
    closeAssignment();
  };

  const resetFoodForm = () => {
    onClearEmbedLocationPreview?.();
    setFoodForm({
      name: '',
      locationType: 'farm',
      networkName: '',
      address: '',
      website: '',
      mapEmbedUrl: '',
      hours: '',
      donationRules: '',
      description: '',
      pickup: true,
      dropoff: false,
    });
    clearPhotoState(setPhoto);
    setShowAddForm(false);
  };

  const submitFood = () => {
    if (!canSubmitFood) return;
    onSubmitFood?.({
      ...foodForm,
      photoFile: photo.file,
      photoWidth: photo.width,
      photoHeight: photo.height,
      name: foodForm.name.trim(),
      website: foodForm.website.trim(),
      mapEmbedUrl: foodForm.mapEmbedUrl.trim(),
      description: foodForm.description.trim(),
    });
    resetFoodForm();
  };

  return (
    <View style={styles.resourcesRoot}>
      <View style={styles.resourceSearchRow}>
        <View style={styles.resourceSearchBox}>
          <TextInput
            style={styles.resourceSearchInput}
            value={query}
            onChangeText={setQuery}
            placeholder={animatedPlaceholder}
            placeholderTextColor={isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.42)'}
            clearButtonMode="while-editing"
            backgroundColor="transparent"
          />
          <Ionicons
            name="search"
            size={16}
            color={isDarkMode ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.42)'}
          />
        </View>
        <TouchableOpacity
          style={styles.resourceAddButton}
          onPress={() => { if (requireContributor()) setShowAddForm(true); }}
          accessibilityLabel="Add a food location"
        >
          <Ionicons name="add" size={20} color="#ffffff" />
        </TouchableOpacity>
      </View>

      <View style={styles.foodToggleWrap}>
        <TextUnderlineToggle
          styles={styles}
          value={activeTab}
          onChange={selectTab}
          options={[
            { id: 'pickup', label: FOOD_HUB.pickup.label },
            { id: 'dropoff', label: FOOD_HUB.dropoff.label },
          ]}
        />
      </View>

      {activeTab === 'dropoff' ? (
        <View style={styles.foodLinksWrap}>
          <Text style={styles.resourceResultsTitle}>More ways to give food</Text>
          <View style={styles.foodLinksRow}>
            {FOOD_GIVE_LINKS.map((link) => (
              <TouchableOpacity
                key={link.id}
                style={styles.foodLinkChip}
                onPress={() => {
                  if (typeof window !== 'undefined') window.open(link.url, '_blank', 'noopener,noreferrer');
                }}
                accessibilityRole="link"
                accessibilityLabel={`${link.label}, opens in a new tab`}
              >
                <Text style={styles.foodLinkChipText} numberOfLines={1}>{link.label}</Text>
                <Ionicons name="open-outline" size={11} color={FOOD_HUB.dropoff.color} />
              </TouchableOpacity>
            ))}
          </View>
        </View>
      ) : null}

      {onSelectDay ? (
        <View style={{ marginTop: 12, marginBottom: 2, gap: 8 }}>
          <Text style={styles.resourceResultsTitle}>{hideDayPicker ? 'This day’s route' : 'Pick a day’s route'}</Text>
          <Text style={styles.resourceFormHelpText}>
            Farms appear along the drive between that day’s venues and lodging, {corridorRadiusMiles} miles off the line.
          </Text>
          {hideDayPicker ? null : routeDays.length === 0 ? (
            <Text style={styles.resourceEmptyText}>
              Add stops to the tour first. Each day with two or more stops (venue, hotel, etc.) gets a searchable route.
            </Text>
          ) : (
            <ScrollView
              horizontal
              {...THIN_SCROLLBAR_PROPS}
              style={{ flexGrow: 0 }}
              contentContainerStyle={{ gap: 8, paddingVertical: 4, paddingRight: 8 }}
              showsHorizontalScrollIndicator={false}
            >
              {routeDays.map((day) => {
                const selected = day.id === selectedDayId;
                const parts = farmDayCardParts(day);
                return (
                  <TouchableOpacity
                    key={day.id}
                    onPress={() => onSelectDay(day.id)}
                    style={[
                      styles.farmDayCard,
                      selected && styles.farmDayCardSelected,
                    ]}
                    accessibilityRole="button"
                    accessibilityState={{ selected }}
                    accessibilityLabel={formatFarmDayChip(day)}
                  >
                    <View style={styles.farmDayCardTop}>
                      <Text style={[styles.farmDayDow, selected && styles.farmDayTextSelected]}>
                        {parts.dow}
                      </Text>
                      {parts.num ? (
                        <Text style={[styles.farmDayNum, selected && styles.farmDayTextSelected]}>
                          {parts.num}
                        </Text>
                      ) : null}
                    </View>
                    <Text style={[styles.farmDayName, selected && styles.farmDayTextSelected]} numberOfLines={1}>
                      {parts.name}
                    </Text>
                    <Text style={[styles.farmDayMeta, selected && styles.farmDayMetaSelected]} numberOfLines={1}>
                      {parts.month && parts.num ? `${parts.month} ${parts.num}` : ''}
                      {parts.month && parts.num && parts.routeCount ? ' · ' : ''}
                      {parts.routeCount ? `${parts.routeCount} route${parts.routeCount === 1 ? '' : 's'}` : 'No route yet'}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          )}
          {(() => {
            const day = routeDays.find((entry) => entry.id === selectedDayId) || routeDays[0];
            if (!day) return null;
            const dayLegs = day.legs || [];
            if (dayLegs.length === 0) {
              return (
                <Text style={styles.resourceEmptyText}>
                  {day.name} needs two mapped stops to search a route. Add a venue or lodging on this day.
                </Text>
              );
            }
            const options = [
              { id: 'all', label: `All ${dayLegs.length} routes this day`, blurb: 'Venues, lodging, and the overnight hop if there is one' },
              ...dayLegs.map((leg) => ({
                id: leg.id,
                label: `${leg.fromName} → ${leg.toName}`,
                blurb: leg.kind === 'inter' ? `Overnight into ${leg.nextDayName || 'the next day'}` : (leg.mode || 'drive'),
              })),
            ];
            return options.map((option) => {
              const selected = (selectedLegId || 'all') === option.id;
              return (
                <TouchableOpacity
                  key={option.id}
                  onPress={() => onSelectLeg?.(option.id)}
                  style={[
                    styles.resourceResultItem,
                    selected && styles.resourceResultItemSelected,
                    { marginBottom: 0 },
                  ]}
                >
                  <View style={styles.resourceResultCopy}>
                    <Text style={styles.resourceResultName} numberOfLines={2}>{option.label}</Text>
                    <Text style={styles.resourceResultDescription} numberOfLines={1}>{option.blurb}</Text>
                  </View>
                  <View style={[styles.resourceSelectMark, selected && styles.resourceSelectMarkSelected]}>
                    {selected ? <Ionicons name="checkmark" size={14} color="#ffffff" /> : null}
                  </View>
                </TouchableOpacity>
              );
            });
          })()}
          {onCorridorRadiusChange ? (
            <View style={{ gap: 6, marginTop: 4 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text style={styles.resourceResultsTitle}>Search radius</Text>
                <Text style={{ fontSize: 12, fontWeight: '700', color: FOOD_HUB.pickup.color }}>
                  {corridorRadiusMiles} mi
                </Text>
              </View>
              {Platform.OS === 'web' ? (
                <input
                  type="range"
                  min={5}
                  max={100}
                  step={5}
                  value={corridorRadiusMiles}
                  onChange={(event) => onCorridorRadiusChange(Number(event.target.value))}
                  style={{ width: '100%', accentColor: FOOD_HUB.pickup.color }}
                />
              ) : null}
            </View>
          ) : null}
        </View>
      ) : null}

      <View style={styles.resourceResultsHeader}>
        <Text style={styles.resourceResultsTitle} numberOfLines={1}>
          {corridorActive
            ? (corridorLoading
              ? 'Searching this route…'
              : `${visibleItems.length} along this route`)
            : `“${queryLabel}” locations`}
        </Text>
        <TouchableOpacity
          onPress={() => setSelectedIds([])}
          disabled={selectedIds.length === 0}
        >
          <Text style={[
            styles.deselectLink,
            selectedIds.length === 0 && styles.deselectLinkDisabled,
          ]}>
            Deselect all
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        {...THIN_SCROLLBAR_PROPS}
        style={styles.resourceResultsScroll}
        contentContainerStyle={styles.resourceResultsContent}
        showsVerticalScrollIndicator
      >
        {visibleItems.length === 0 ? (
          <Text style={styles.resourceEmptyText}>
            {corridorActive
              ? (corridorLoading
                ? 'Searching along this day’s route…'
                : (corridorMessage || `No ${FOOD_HUB[activeTab].label.toLowerCase()} within ${corridorRadiusMiles} miles of the selected route.`))
              : `Pick a day and a route above to find ${FOOD_HUB[activeTab].label.toLowerCase()} along the drive.`}
          </Text>
        ) : visibleItems.map((item) => {
          const selected = selectedSet.has(item.id);
          return (
            <TouchableOpacity
              key={item.id}
              style={[
                styles.resourceResultItem,
                selected && styles.resourceResultItemSelected,
              ]}
              onPress={() => setSelectedIds((prev) =>
                prev.includes(item.id)
                  ? prev.filter((id) => id !== item.id)
                  : [...prev, item.id]
              )}
              accessibilityState={{ selected }}
            >
              <View style={styles.resourceResultCopy}>
                <View style={styles.foodItemTitleRow}>
                  <Ionicons
                    name={FOOD_HUB[activeTab].icon}
                    size={14}
                    color={FOOD_HUB[activeTab].color}
                    style={{ marginRight: 5 }}
                  />
                  <Text style={styles.resourceResultName} numberOfLines={1}>
                    {item.name}
                  </Text>
                  <TouchableOpacity
                    style={styles.foodItemAddButton}
                    onPress={(event) => {
                      event?.stopPropagation?.();
                      setAssignmentItem(item);
                    }}
                  >
                    <Text style={styles.foodItemAddButtonText}>ADD</Text>
                  </TouchableOpacity>
                </View>
                <Text style={styles.resourceResultDescription} numberOfLines={2}>
                  {[
                    Number.isFinite(Number(item.milesFromRoute))
                      ? `${Number(item.milesFromRoute).toFixed(1)} mi off route`
                      : '',
                    item.description || item.location || item.address || 'Food location',
                  ].filter(Boolean).join(' · ')}
                </Text>
              </View>
              <View style={[styles.resourceSelectMark, selected && styles.resourceSelectMarkSelected]}>
                {selected ? <Ionicons name="checkmark" size={14} color="#ffffff" /> : null}
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      <FullscreenSafeModal
        visible={Boolean(assignmentItem)}
        onRequestClose={closeAssignment}
        hostRef={fullscreenHostRef}
      >
          <View style={styles.sideModalBackdrop}>
          <View style={styles.sideModalCard}>
            <Text style={styles.sideModalTitle}>Add to route and rider?</Text>
            <Text style={styles.sideModalSubtitle}>{assignmentItem?.name}</Text>
            {[
              { label: 'Route', value: assignRoute, setValue: setAssignRoute },
              { label: 'Rider', value: assignRider, setValue: setAssignRider },
            ].map((option) => (
              <TouchableOpacity
                key={option.label}
                style={styles.sideModalCheckRow}
                onPress={() => option.setValue((value) => !value)}
              >
                <View style={[
                  styles.sideModalCheckbox,
                  option.value && styles.sideModalCheckboxSelected,
                ]}>
                  {option.value ? <Ionicons name="checkmark" size={14} color="#ffffff" /> : null}
                </View>
                <Text style={styles.sideModalCheckLabel}>{option.label}</Text>
              </TouchableOpacity>
            ))}
            <View style={styles.sideModalActions}>
              <TouchableOpacity style={styles.sideModalNoButton} onPress={closeAssignment}>
                <Text style={styles.sideModalNoText}>No</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.sideModalYesButton,
                  !assignRoute && !assignRider && styles.sideModalButtonDisabled,
                ]}
                onPress={submitAssignment}
                disabled={!assignRoute && !assignRider}
              >
                <Text style={styles.sideModalYesText}>Yes</Text>
              </TouchableOpacity>
            </View>
          </View>
          </View>
      </FullscreenSafeModal>

      <FullscreenSafeModal
        visible={showAddForm}
        onRequestClose={resetFoodForm}
        hostRef={fullscreenHostRef}
      >
          <View style={styles.sideModalBackdrop}>
          <View style={styles.sideModalCard}>
            <ScrollView
              {...THIN_SCROLLBAR_PROPS}
              contentContainerStyle={styles.resourceFormContent}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
            >
              <View style={styles.resourceFormHeader}>
                <Text style={styles.sideModalTitle}>ADD TO MAP</Text>
                <TouchableOpacity onPress={resetFoodForm} accessibilityLabel="Close form">
                  <Text style={styles.resourceFormClose}>✕</Text>
                </TouchableOpacity>
              </View>

              <View style={styles.resourceFormCategoryBanner}>
                <Ionicons name="restaurant-outline" size={26} color={LAYER_COLOR.foodBank} />
                <View>
                  <Text style={styles.resourceFormCategoryLabel}>Location Type (select)</Text>
                  <Text style={styles.resourceFormCategoryValue}>
                    {foodForm.locationType === 'fridge' ? 'FRIDGE / PANTRY' : 'FARM'}
                  </Text>
                </View>
              </View>

              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {[
                  { id: 'farm', label: 'FARM', pickup: true, dropoff: false },
                  { id: 'fridge', label: 'FRIDGE / PANTRY', pickup: false, dropoff: true },
                ].map((option) => (
                  <TouchableOpacity
                    key={option.id}
                    style={[
                      styles.travelChip,
                      foodForm.locationType === option.id && { backgroundColor: LAYER_COLOR.foodBank, borderColor: LAYER_COLOR.foodBank },
                    ]}
                    onPress={() => setFoodForm((prev) => ({
                      ...prev,
                      locationType: option.id,
                      pickup: option.pickup,
                      dropoff: option.dropoff,
                    }))}
                  >
                    <Text style={[
                      styles.travelChipText,
                      foodForm.locationType === option.id && styles.travelChipTextActive,
                    ]}>
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={styles.resourceFormHelpText}>
                Suggest a farm or fridge / pantry for the map. Admins will review before it appears.
              </Text>

              <Text style={styles.sideModalFieldLabel}>Location Name *</Text>
              <TextInput
                style={styles.sideModalInput}
                value={foodForm.name}
                onChangeText={(name) => setFoodForm((prev) => ({ ...prev, name }))}
                placeholder="e.g. Green Bowl Kitchen"
                placeholderTextColor={placeholderColor}
              />

              <Text style={styles.sideModalFieldLabel}>Network Name</Text>
              <TextInput
                style={styles.sideModalInput}
                value={foodForm.networkName}
                onChangeText={(networkName) => setFoodForm((prev) => ({ ...prev, networkName }))}
                placeholder="Optional network or org"
                placeholderTextColor={placeholderColor}
              />

              <Text style={styles.sideModalFieldLabel}>Location Address</Text>
              <TextInput
                style={styles.sideModalInput}
                value={foodForm.address}
                onChangeText={(address) => setFoodForm((prev) => ({ ...prev, address }))}
                placeholder="city, state, zip, country"
                placeholderTextColor={placeholderColor}
              />

              <MapEmbedField
                styles={styles}
                placeholderColor={placeholderColor}
                value={foodForm.mapEmbedUrl}
                onChangeText={(mapEmbedUrl) => setFoodForm((prev) => ({ ...prev, mapEmbedUrl }))}
                embedStatus={embedStatus}
              />

              <Text style={styles.sideModalFieldLabel}>URL (optional)</Text>
              <TextInput
                style={styles.sideModalInput}
                value={foodForm.website}
                onChangeText={(website) => setFoodForm((prev) => ({ ...prev, website }))}
                placeholder="https://"
                autoCapitalize="none"
                placeholderTextColor={placeholderColor}
              />

              <Text style={styles.sideModalFieldLabel}>Location Operational Hours</Text>
              <TextInput
                style={styles.sideModalInput}
                value={foodForm.hours}
                onChangeText={(hours) => setFoodForm((prev) => ({ ...prev, hours }))}
                placeholder="e.g. Sat 10–2"
                placeholderTextColor={placeholderColor}
              />

              <Text style={styles.sideModalFieldLabel}>Donation Location Rules and Requests</Text>
              <TextInput
                style={[styles.sideModalInput, styles.sideModalTextArea]}
                value={foodForm.donationRules}
                onChangeText={(donationRules) => setFoodForm((prev) => ({ ...prev, donationRules }))}
                placeholder="What to bring / what not to leave"
                placeholderTextColor={placeholderColor}
                multiline
              />

              <Text style={styles.sideModalFieldLabel}>Notes</Text>
              <TextInput
                style={[styles.sideModalInput, styles.sideModalTextArea]}
                value={foodForm.description}
                onChangeText={(description) => setFoodForm((prev) => ({ ...prev, description }))}
                placeholder="Anything else people should know"
                placeholderTextColor={placeholderColor}
                multiline
              />

              <CoverPhotoPicker
                isDarkMode={isDarkMode}
                styles={styles}
                photo={photo}
                onSelectFile={(file) => selectPhotoFromFile(file, setPhoto)}
                onClear={() => clearPhotoState(setPhoto)}
                subjectLabel="location"
              />

              <Text style={styles.sideModalFieldLabel}>Pickup or dropoff</Text>
              <Text style={styles.resourceFormHelpText}>
                Does this location provide pickup, dropoff, or both?
              </Text>
              {[
                { key: 'pickup', label: 'Pickup' },
                { key: 'dropoff', label: 'Dropoff' },
              ].map((option) => (
                <TouchableOpacity
                  key={option.key}
                  style={styles.sideModalCheckRow}
                  onPress={() => setFoodForm((prev) => ({
                    ...prev,
                    [option.key]: !prev[option.key],
                  }))}
                >
                  <View style={[
                    styles.sideModalCheckbox,
                    foodForm[option.key] && styles.sideModalCheckboxSelected,
                  ]}>
                    {foodForm[option.key]
                      ? <Ionicons name="checkmark" size={14} color="#ffffff" />
                      : null}
                  </View>
                  <Text style={styles.sideModalCheckLabel}>{option.label}</Text>
                </TouchableOpacity>
              ))}

              <TouchableOpacity
                style={[
                  styles.resourceSubmitButton,
                  !canSubmitFood && styles.sideModalButtonDisabled,
                ]}
                onPress={submitFood}
                disabled={!canSubmitFood}
              >
                <Text style={styles.resourceSubmitButtonText}>SUBMIT FOR REVIEW</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
          </View>
      </FullscreenSafeModal>
    </View>
  );
}

function DirectoryPanel({
  isDarkMode,
  styles,
  venueOptions = [],
  selectedIds = [],
  onToggleItem,
  onKeepSelectedIds,
  routeRadius = null,
}) {
  const requireContributor = useMapContributorGate();
  const { user } = useUser();
  const inRouteRadius = useRouteRadiusFilter(routeRadius, 'directory');
  const { openMessaging } = useCommunityModal();
  const { allEntities = [], entities = [], loading: communityLoading } = useCommunity();
  const [mode, setMode] = useState('directory');
  const [searchQuery, setSearchQuery] = useState('');
  const [gearListings, setGearListings] = useState([]);
  const [gearLoading, setGearLoading] = useState(false);
  const isGearMode = mode === 'gearSwap';
  const isAdmin = user?.userType === 'admin';

  useEffect(() => {
    let cancelled = false;
    const loadGear = async () => {
      setGearLoading(true);
      try {
        const snapshot = await getDocs(
          query(collection(getFirestore(), 'gearSwapPosts'), orderBy('createdAt', 'desc'), limit(100))
        );
        if (cancelled) return;
        setGearListings(snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        })));
      } catch (error) {
        console.error('Failed to load gear swap listings:', error);
        if (!cancelled) setGearListings([]);
      } finally {
        if (!cancelled) setGearLoading(false);
      }
    };
    loadGear();
    return () => {
      cancelled = true;
    };
  }, []);

  const directoryItems = (allEntities.length > 0 ? allEntities : entities).filter((entity) => (
    isAdmin || !entity.adminOnly
  ));
  const sourceItems = isGearMode ? gearListings : directoryItems;
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const visibleItems = sourceItems
    .filter((item) => !normalizedQuery || getDirectorySearchText(item).includes(normalizedQuery))
    .filter((item) => inRouteRadius(getPostPinCoordinates(item, venueOptions)));
  const loading = isGearMode ? gearLoading : communityLoading;
  const selectedSet = new Set(selectedIds);
  useRoutePreviewPins(
    routeRadius,
    'directory',
    normalizedQuery
      ? visibleItems.map((item) => toPreviewPin(
        item.id,
        item.displayName || item.postContent || item.location || 'Post',
        getPostPinCoordinates(item, venueOptions),
      )).filter(Boolean)
      : [],
  );

  useEffect(() => {
    if (!onKeepSelectedIds || selectedIds.length === 0) return;
    const sourceIds = new Set(sourceItems.map((item) => item.id));
    const visibleIds = new Set(visibleItems.map((item) => item.id));
    const keptIds = selectedIds.filter((id) => !sourceIds.has(id) || visibleIds.has(id));
    if (keptIds.length !== selectedIds.length) onKeepSelectedIds(keptIds);
  }, [sourceItems, visibleItems, selectedIds, onKeepSelectedIds]);

  const toggleItem = (item) => {
    const coords = getPostPinCoordinates(item, venueOptions);
    onToggleItem?.({
      id: item.id,
      name: isGearMode
        ? (item.displayName || item.postContent || 'Post')
        : (item.postContent || item.location || item.displayName || 'Post'),
      latitude: coords?.lat ?? null,
      longitude: coords?.lng ?? null,
    });
  };

  const messageItem = (item) => {
    if (!requireContributor()) return;
    const targetId = item?.postAsOrganizationId || item?.userId || null;
    if (!targetId) return;
    const targetName =
      item?.postAsOrganizationName ||
      item?.displayName ||
      item?.username ||
      item?.firstName ||
      'Community Member';
    openMessaging(targetId, targetName);
  };

  return (
    <View style={styles.resourcesRoot}>
      <View style={styles.resourceSearchRow}>
        <View style={styles.resourceSearchBox}>
          <TextInput
            style={styles.resourceSearchInput}
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder={isGearMode ? 'Search for gear...' : 'Search posts...'}
            placeholderTextColor={isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.42)'}
            clearButtonMode="while-editing"
            backgroundColor="transparent"
          />
          <Ionicons
            name="search"
            size={16}
            color={isDarkMode ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.42)'}
          />
        </View>
      </View>

      <View style={styles.foodToggleWrap}>
        <TextUnderlineToggle
          styles={styles}
          value={mode}
          onChange={setMode}
          options={[
            { id: 'directory', label: 'Directory' },
            { id: 'gearSwap', label: 'Gear Swap' },
          ]}
        />
      </View>

      <View style={styles.resourceResultsHeader}>
        <Text style={styles.resourceResultsTitle} numberOfLines={1}>
          {loading
            ? 'Loading…'
            : `${visibleItems.length} ${visibleItems.length === 1 ? 'post' : 'posts'}`}
        </Text>
      </View>

      <ScrollView
        {...THIN_SCROLLBAR_PROPS}
        style={styles.resourceResultsScroll}
        contentContainerStyle={styles.directoryResultsContent}
        showsVerticalScrollIndicator
      >
        {loading ? (
          <ActivityIndicator size="small" color="#e67e22" style={styles.directoryLoading} />
        ) : visibleItems.length === 0 ? (
          <Text style={styles.resourceEmptyText}>
            {normalizedQuery
              ? 'No matching posts.'
              : isGearMode
                ? 'Gear Swap posts will appear here.'
                : 'Community Directory posts will appear here.'}
          </Text>
        ) : visibleItems.map((item) => {
          const selected = selectedSet.has(item.id);
          return (
            <View
              key={item.id}
              style={[
                styles.directoryCardWrap,
                selected && styles.directoryCardWrapSelected,
              ]}
            >
              <CommunityPostCard
                compact
                post={{
                  ...item,
                  postContent: isGearMode
                    ? (item.displayName || item.postContent || 'Post')
                    : item.postContent,
                }}
                showFolderButton={false}
                showMessageButton
                onMessagePress={() => messageItem(item)}
                showHeadlinePlaceholder={!isGearMode}
                compactBadge={isGearMode && item.listingType
                  ? {
                    label: item.listingType,
                    price: item.listingType === 'SALE' ? item.price : null,
                    color: GEAR_LISTING_TYPE_COLORS[item.listingType] || '#e67e22',
                  }
                  : null}
                onPress={() => toggleItem(item)}
              />
            </View>
          );
        })}
      </ScrollView>
      <RouteRadiusControl styles={styles} routeRadius={routeRadius} section="directory" />
    </View>
  );
}

const VENUE_SECTIONS = [
  {
    title: 'Basic Information',
    fields: [
      { label: 'Venue Name', key: 'name', placeholder: 'Enter venue name', required: true },
      { label: 'City', key: 'city', placeholder: 'Enter city', required: true },
      { label: 'State', key: 'state', placeholder: 'Enter state / province', required: true },
      { label: 'Google Maps link or embed (optional)', key: 'mapEmbedLink', type: 'mapEmbed' },
      { label: 'Website', key: 'website', placeholder: 'https://…' },
      { label: 'Capacity', key: 'capacity', placeholder: 'e.g. 2500' },
      { label: 'Affiliate', key: 'affiliate', placeholder: 'e.g. AEG, Live Nation' },
    ],
  },
  {
    title: 'Technical Details',
    fields: [
      { label: 'Stage Dimensions', key: 'stage', placeholder: 'e.g. 40ft wide × 30ft deep' },
      { label: 'Power', key: 'power', placeholder: 'e.g. 400A 3-phase' },
      { label: 'Lighting Control', key: 'lightingControl', placeholder: 'e.g. GrandMA3' },
      { label: 'Lighting Fixtures', key: 'lightingFixtures', placeholder: 'Fixture inventory…' },
      { label: 'Video', key: 'video', placeholder: 'e.g. LED wall, projectors…' },
      { label: 'FOH', key: 'foh', placeholder: 'e.g. DiGiCo SD12' },
      { label: 'PA System', key: 'pa', placeholder: 'e.g. L-Acoustics K2' },
      { label: 'Monitors', key: 'monitors', placeholder: 'e.g. L-Acoustics X15' },
    ],
  },
  {
    title: 'Facilities',
    fields: [
      { label: 'ADA Accessibility (Artist)', key: 'adaAccessibilityArtist', placeholder: 'ADA details for artists…' },
      { label: 'Backstage', key: 'backstage', placeholder: 'Dressing rooms, green rooms…' },
      { label: 'Backline', key: 'backline', placeholder: 'Available backline…' },
      { label: 'Merch Space', key: 'merch', placeholder: 'Merch table / area details…' },
      { label: 'Parking', key: 'parking', placeholder: 'Bus, trailer, load-in parking…' },
      { label: 'Shore Power', key: 'shorePower', placeholder: 'Shore power availability…' },
    ],
  },
  {
    title: 'Additional Information',
    fields: [
      { label: 'Spec Sheet Link', key: 'specSheetLink', placeholder: 'URL to tech rider / spec sheet' },
      { label: 'Notes', key: 'notes', placeholder: 'Anything else useful…', multiline: true },
    ],
  },
];

const EMPTY_VENUE_FORM = {
  name: '', city: '', state: '', website: '', capacity: '', affiliate: '',
  stage: '', power: '', lightingControl: '', lightingFixtures: '', video: '',
  foh: '', pa: '', monitors: '',
  adaAccessibilityArtist: '', backstage: '', backline: '', merch: '', parking: '', shorePower: '',
  specSheetLink: '', mapEmbedLink: '', notes: '',
};

// What the map can actually accept. Each entry routes to the rail section that
// already owns that data type, rather than duplicating its submission form.
const ADD_TO_MAP_CHOICES = [
  {
    id: 'resource',
    itemId: 'leaf',
    action: 'add',
    label: 'Resource',
    blurb: 'Printers, repair, gear, services',
    icon: 'leaf',
    color: LAYER_COLOR.resource,
  },
  {
    id: 'travel',
    itemId: 'plane',
    action: 'add',
    label: 'Travel destination',
    blurb: 'Lodging, parks, transit, wellness',
    icon: 'airplane',
    color: LAYER_COLOR.travel,
  },
  {
    id: 'food',
    itemId: 'food',
    action: 'add',
    label: 'Food Hub',
    blurb: 'Farms (pickup) or Food Banks (drop off)',
    icon: 'swap-vertical',
    color: LAYER_COLOR.farm,
  },
  {
    // REQ-065: flyers post through the Event Flyers section's existing form.
    id: 'flyer',
    itemId: 'poster',
    action: 'add',
    label: 'Event Flyer',
    blurb: 'Shows, benefits, community events',
    icon: 'newspaper',
    color: LAYER_COLOR.flyer,
  },
];

// REQ-065: Add to Map is for resources only. New venues are suggested from the
// existing Add Venue page, so this panel no longer links to the venue form.
function AddToMapPanel({ isDarkMode, styles, onNavigate }) {
  const requireContributor = useMapContributorGate();
  const sub = isDarkMode ? 'rgba(255,255,255,0.56)' : 'rgba(0,0,0,0.52)';
  const ink = isDarkMode ? '#ffffff' : '#14141a';
  const line = isDarkMode ? 'rgba(255,255,255,0.12)' : '#e4e4df';

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ padding: 16, paddingBottom: 32, gap: 10 }}
      {...THIN_SCROLLBAR_PROPS}
    >
      <Text style={[styles.sideModalTitle, { marginBottom: 2 }]}>Add to map</Text>
      <Text style={{ fontSize: 12, color: sub, lineHeight: 17, marginBottom: 6 }}>
        Pick what you're adding. Each opens the section that owns it.
      </Text>

      {ADD_TO_MAP_CHOICES.map((choice) => (
        <TouchableOpacity
          key={choice.id}
          onPress={() => { if (requireContributor()) onNavigate?.(choice.itemId, choice.action); }}
          accessibilityRole="button"
          accessibilityLabel={`Add ${choice.label}`}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
            padding: 13,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: line,
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#fffffe',
          }}
        >
          <View style={{
            width: 34, height: 34, borderRadius: 17,
            alignItems: 'center', justifyContent: 'center',
            backgroundColor: choice.color + '1F',
          }}>
            <Ionicons name={choice.icon} size={17} color={choice.color} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 13.5, fontWeight: '600', color: ink }}>{choice.label}</Text>
            <Text style={{ fontSize: 11.5, color: sub, marginTop: 1 }} numberOfLines={1}>
              {choice.blurb}
            </Text>
          </View>
          <Ionicons name="chevron-forward" size={16} color={sub} />
        </TouchableOpacity>
      ))}
    </ScrollView>
  );
}

// Kept intact but unreachable from Add to Map since REQ-065 (venues come from the
// Add Venue page). Only shown if something sets showVenueForm again.
function SuggestVenuePanel({
  isDarkMode,
  styles,
  onSubmitVenue,
  onEmbedLocationPreview,
  onClearEmbedLocationPreview,
}) {
  const [form, setForm] = useState(EMPTY_VENUE_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const placeholderColor = isDarkMode ? 'rgba(255,255,255,0.34)' : 'rgba(0,0,0,0.34)';
  const canSubmit = Boolean(form.name.trim() && form.city.trim() && form.state.trim());
  const { embedStatus } = useMapEmbedResolve({
    embed: form.mapEmbedLink,
    name: form.name,
    city: form.city,
    state: form.state,
    onPreview: onEmbedLocationPreview,
    onClear: onClearEmbedLocationPreview,
  });

  const resetForm = () => {
    onClearEmbedLocationPreview?.();
    setForm(EMPTY_VENUE_FORM);
    setSubmitted(false);
  };

  const submitVenue = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await onSubmitVenue?.({ ...form });
      setSubmitted(true);
    } catch (e) {
      console.error('SuggestVenuePanel submit error', e);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, gap: 14 }}>
        <Ionicons name="checkmark-circle" size={48} color="#00A575" />
        <Text style={{ fontSize: 15, fontWeight: '700', color: isDarkMode ? '#ffffff' : '#1a1a1a', textAlign: 'center' }}>
          Venue submitted!
        </Text>
        <Text style={{ fontSize: 12, color: isDarkMode ? 'rgba(255,255,255,0.56)' : 'rgba(0,0,0,0.52)', textAlign: 'center', lineHeight: 18 }}>
          An admin will review and approve it before it appears on the map.
        </Text>
        <View style={{
          flexDirection: 'row', alignItems: 'center', gap: 6,
          borderWidth: 1, borderStyle: 'dashed', borderColor: '#c08133',
          backgroundColor: 'rgba(192,129,51,0.07)',
          borderRadius: 8, paddingVertical: 7, paddingHorizontal: 12,
        }}>
          <Ionicons name="time-outline" size={13} color="#c08133" />
          <Text style={{ fontSize: 11.5, fontWeight: '600', color: '#c08133' }}>
            Pending credits — earned from map info, apply to Map + Calendar subscriptions
          </Text>
        </View>
        <TouchableOpacity
          onPress={resetForm}
          style={{
            marginTop: 8, paddingVertical: 10, paddingHorizontal: 22,
            borderRadius: 8, backgroundColor: isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.07)',
          }}
        >
          <Text style={{ fontSize: 13, fontWeight: '600', color: isDarkMode ? '#ffffff' : '#1a1a1a' }}>
            Submit another
          </Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={[styles.resourceFormContent, { padding: 16, paddingBottom: 32 }]}
      {...THIN_SCROLLBAR_PROPS}
      keyboardShouldPersistTaps="handled"
    >
      {/* Header */}
      <View style={styles.resourceFormHeader}>
        <Text style={[styles.sideModalTitle, { flex: 1 }]}>Add a Venue</Text>
      </View>

      {/* Category banner */}
      <View style={styles.resourceFormCategoryBanner}>
        <Ionicons name="business-outline" size={26} color="#e67e22" />
        <View>
          <Text style={styles.resourceFormCategoryLabel}>Submission type</Text>
          <Text style={styles.resourceFormCategoryValue}>New Venue</Text>
        </View>
      </View>

      <Text style={styles.resourceFormHelpText}>
        Suggest a venue for the map. Admins will review before it appears publicly.
        Fields marked * are required.
      </Text>

      {/* Sectioned fields */}
      {VENUE_SECTIONS.map((section) => (
        <View key={section.title}>
          <Text style={[styles.resourceFormCategoryLabel, { marginTop: 18, marginBottom: 6 }]}>
            {section.title}
          </Text>
          {section.fields.map(({ label, key, placeholder, required, multiline, type }) => (
            <View key={key} style={{ marginBottom: 10 }}>
              {type === 'mapEmbed' ? (
                <MapEmbedField
                  label={`${label}${required ? ' *' : ''}`}
                  styles={styles}
                  placeholderColor={placeholderColor}
                  value={form[key]}
                  onChangeText={(val) => setForm((prev) => ({ ...prev, [key]: val }))}
                  embedStatus={embedStatus}
                  placeholder={placeholder || 'Paste a Google Maps link or embed'}
                />
              ) : (
                <>
                  <Text style={styles.sideModalFieldLabel}>
                    {label}{required ? ' *' : ''}
                  </Text>
                  <TextInput
                    style={[
                      styles.sideModalInput,
                      multiline && { minHeight: 72, textAlignVertical: 'top', paddingTop: 8 },
                    ]}
                    value={form[key]}
                    onChangeText={(val) => setForm((prev) => ({ ...prev, [key]: val }))}
                    placeholder={placeholder}
                    placeholderTextColor={placeholderColor}
                    multiline={!!multiline}
                  />
                </>
              )}
            </View>
          ))}
        </View>
      ))}

      {/* Submit */}
      <TouchableOpacity
        style={[
          styles.resourceSubmitButton,
          (!canSubmit || submitting) && styles.sideModalButtonDisabled,
        ]}
        onPress={submitVenue}
        disabled={!canSubmit || submitting}
      >
        {submitting ? (
          <ActivityIndicator size="small" color="#ffffff" />
        ) : (
          <Text style={styles.resourceSubmitButtonText}>SUBMIT FOR REVIEW</Text>
        )}
      </TouchableOpacity>
    </ScrollView>
  );
}

const CONTACT_SEARCH_PLACEHOLDERS = [
  'Name',
  'Venue',
  'Phone',
  'Email',
  'Notes',
];

function SavedContactsPanel({ isDarkMode, styles }) {
  const [query, setQuery] = useState('');
  const [contacts, setContacts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [photo, setPhoto] = useState(EMPTY_PHOTO);
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    associatedVenues: '',
    radiusMiles: '',
    notes: '',
  });
  const animatedPlaceholder = useTypingAnimation(
    CONTACT_SEARCH_PLACEHOLDERS,
    80,
    30,
    1300,
  );

  const normalizedQuery = query.trim().toLowerCase();
  const visibleContacts = contacts.filter((c) => {
    if (!normalizedQuery) return true;
    return [c.name, c.phone, c.email, c.associatedVenues, c.notes]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(normalizedQuery);
  });

  const resetForm = () => {
    setContactForm({ name: '', phone: '', email: '', associatedVenues: '', radiusMiles: '', notes: '' });
    clearPhotoState(setPhoto);
    setShowAddForm(false);
  };

  const submitContact = () => {
    if (!contactForm.name.trim()) return;
    setContacts((prev) => [
      ...prev,
      {
        id: String(Date.now()),
        ...contactForm,
        name: contactForm.name.trim(),
        photoPreview: photo.preview || null,
      },
    ]);
    resetForm();
  };

  if (showAddForm) {
    return (
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ padding: 16, paddingBottom: 32 }}
        {...THIN_SCROLLBAR_PROPS}
      >
        {/* Header */}
        <View style={styles.resourceFormHeader}>
          <Text style={[styles.resourceFormTitle || styles.resourceResultsTitle, { flex: 1, fontSize: 15, fontWeight: '700' }]}>
            Add Contact
          </Text>
          <TouchableOpacity onPress={resetForm}>
            <Text style={styles.resourceFormClose}>✕</Text>
          </TouchableOpacity>
        </View>

        {/* Cover Photo */}
        <CoverPhotoPicker
          isDarkMode={isDarkMode}
          styles={styles}
          photo={photo}
          onSelectFile={(file) => selectPhotoFromFile(file, setPhoto)}
          onClear={() => clearPhotoState(setPhoto)}
        />

        {/* Fields */}
        {[
          { label: 'Name', key: 'name', placeholder: 'Full name', required: true },
          { label: 'Phone', key: 'phone', placeholder: 'Phone number' },
          { label: 'Email', key: 'email', placeholder: 'Email address' },
          { label: 'Associated Venues', key: 'associatedVenues', placeholder: 'Venue names…' },
          { label: 'Radius of show / travel (mi)', key: 'radiusMiles', placeholder: 'e.g. 25' },
          { label: 'Notes', key: 'notes', placeholder: 'Any notes…', multiline: true },
        ].map(({ label, key, placeholder, required, multiline }) => (
          <View key={key} style={styles.resourceFormFieldWrap || { marginTop: 12 }}>
            <Text style={styles.resourceFormFieldLabel || { fontSize: 11, fontWeight: '600', marginBottom: 4, color: isDarkMode ? 'rgba(255,255,255,0.56)' : 'rgba(0,0,0,0.52)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {label}{required ? ' *' : ''}
            </Text>
            <TextInput
              style={[
                styles.resourceFormInput || {
                  borderRadius: 7,
                  paddingHorizontal: 10,
                  paddingVertical: 8,
                  fontSize: 13,
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
                  color: isDarkMode ? '#ffffff' : '#1a1a1a',
                },
                multiline && { minHeight: 70, textAlignVertical: 'top', paddingTop: 8 },
              ]}
              value={contactForm[key]}
              onChangeText={(val) => setContactForm((prev) => ({ ...prev, [key]: val }))}
              placeholder={placeholder}
              placeholderTextColor={isDarkMode ? 'rgba(255,255,255,0.34)' : 'rgba(0,0,0,0.34)'}
              multiline={!!multiline}
            />
          </View>
        ))}

        {/* Submit */}
        <TouchableOpacity
          style={[
            styles.resourceSubmitBtn || {
              marginTop: 20,
              paddingVertical: 13,
              borderRadius: 8,
              alignItems: 'center',
              backgroundColor: '#4f7cff',
            },
            !contactForm.name.trim() && (styles.resourceSubmitBtnDisabled || { opacity: 0.45 }),
          ]}
          onPress={submitContact}
          disabled={!contactForm.name.trim()}
        >
          <Text style={styles.resourceSubmitBtnText || { color: '#ffffff', fontWeight: '700', fontSize: 13, letterSpacing: 0.5 }}>
            SAVE CONTACT
          </Text>
        </TouchableOpacity>
      </ScrollView>
    );
  }

  return (
    <View style={styles.resourcesRoot}>
      {/* Search + Add row */}
      <View style={styles.resourceSearchRow}>
        <View style={styles.resourceSearchBox}>
          <TextInput
            style={styles.resourceSearchInput}
            value={query}
            onChangeText={setQuery}
            placeholder={animatedPlaceholder}
            placeholderTextColor={isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.42)'}
            clearButtonMode="while-editing"
            backgroundColor="transparent"
          />
          <Ionicons
            name="search"
            size={16}
            color={isDarkMode ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.42)'}
          />
        </View>
        <TouchableOpacity
          style={styles.resourceAddButton}
          onPress={() => setShowAddForm(true)}
          accessibilityLabel="Add a contact"
        >
          <Ionicons name="add" size={20} color="#ffffff" />
        </TouchableOpacity>
      </View>

      {/* Tab-like label row */}
      <View style={styles.foodToggleWrap}>
        <TextUnderlineToggle
          styles={styles}
          value="personal"
          onChange={() => {}}
          options={[{ id: 'personal', label: 'Personal' }]}
        />
      </View>

      {/* Count */}
      <View style={styles.resourceResultsHeader}>
        <Text style={styles.resourceResultsTitle} numberOfLines={1}>
          {visibleContacts.length} contact{visibleContacts.length !== 1 ? 's' : ''}
        </Text>
      </View>

      {/* Contact list */}
      <ScrollView style={{ flex: 1 }} {...THIN_SCROLLBAR_PROPS}>
        {visibleContacts.length === 0 ? (
          <Text style={[styles.resourceResultsTitle, { opacity: 0.5, marginTop: 24, textAlign: 'center' }]}>
            No contacts yet.{'\n'}Tap + to add one.
          </Text>
        ) : (
          visibleContacts.map((c) => (
            <View
              key={c.id}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                paddingVertical: 9,
                borderBottomWidth: 1,
                borderBottomColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
              }}
            >
              {c.photoPreview ? (
                <Image
                  source={{ uri: c.photoPreview }}
                  style={{ width: 38, height: 38, borderRadius: 19 }}
                />
              ) : (
                <View
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 19,
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Ionicons name="person" size={18} color={isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)'} />
                </View>
              )}
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: isDarkMode ? '#ffffff' : '#1a1a1a' }} numberOfLines={1}>
                  {c.name}
                </Text>
                {!!c.phone && (
                  <Text style={{ fontSize: 11, color: isDarkMode ? 'rgba(255,255,255,0.52)' : 'rgba(0,0,0,0.52)' }} numberOfLines={1}>
                    {c.phone}
                  </Text>
                )}
                {!!c.email && (
                  <Text style={{ fontSize: 11, color: isDarkMode ? 'rgba(255,255,255,0.52)' : 'rgba(0,0,0,0.52)' }} numberOfLines={1}>
                    {c.email}
                  </Text>
                )}
                {!!c.associatedVenues && (
                  <Text style={{ fontSize: 10, color: isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.38)' }} numberOfLines={1}>
                    {c.associatedVenues}
                  </Text>
                )}
                {!!c.radiusMiles && (
                  <Text style={{ fontSize: 10, color: isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.38)' }} numberOfLines={1}>
                    Within {c.radiusMiles} mi of show / travel
                  </Text>
                )}
              </View>
            </View>
          ))
        )}
      </ScrollView>
    </View>
  );
}

const EMPTY_EDU_FORM = {
  name: '',
  locationType: '',
  resourcesProvided: '',
  website: '',
  mapEmbedLink: '',
};

const EMPTY_FLYER_FORM = {
  title: '',
  date: '',
  link: '',
  ticketLink: '',
  organizedBy: '',
  venueId: '',
  venueName: '',
  details: '',
};

function venueOffersTraining(venue) {
  const raw = venue?.consistentTraining ?? venue?.hasConsistentTraining ?? venue?.employeeTraining;
  if (raw === true) return true;
  const value = String(raw || '').trim().toLowerCase();
  return value === 'yes' || value === 'true';
}

function LearnPanel({
  isDarkMode,
  styles,
  venues = [],
  selectedIds = [],
  onToggleVenue,
  onDeselectAll,
  onSubmitEducation,
  onEmbedLocationPreview,
  onClearEmbedLocationPreview,
  routeRadius = null,
}) {
  const requireContributor = useMapContributorGate();
  const [showForm, setShowForm] = useState(false);
  const [trainingOnly, setTrainingOnly] = useState(false);
  const [venueQuery, setVenueQuery] = useState('');
  const inRouteRadius = useRouteRadiusFilter(routeRadius, 'learn');
  const [form, setForm] = useState(EMPTY_EDU_FORM);
  const [busy, setBusy] = useState(false);
  const placeholderColor = isDarkMode ? 'rgba(255,255,255,0.36)' : 'rgba(0,0,0,0.36)';
  const { embedStatus } = useMapEmbedResolve({
    embed: showForm ? form.mapEmbedLink : '',
    name: form.name,
    onPreview: onEmbedLocationPreview,
    onClear: onClearEmbedLocationPreview,
  });
  const normalizedVenueQuery = venueQuery.trim().toLowerCase();
  const visibleVenues = venues
    .filter((venue) => !trainingOnly || venueOffersTraining(venue))
    .filter((venue) => !normalizedVenueQuery || [venue.name, venue.city, venue.state]
      .filter(Boolean).join(' ').toLowerCase().includes(normalizedVenueQuery))
    .filter(inRouteRadius)
    .slice(0, 80);
  const selectedSet = new Set(selectedIds);
  useRoutePreviewPins(
    routeRadius,
    'learn',
    (normalizedVenueQuery || trainingOnly)
      ? visibleVenues.map((venue) => toPreviewPin(venue.id, venue.name, venue)).filter(Boolean)
      : [],
  );
  const canSend = Boolean(form.name.trim());

  const resetForm = () => {
    onClearEmbedLocationPreview?.();
    setForm(EMPTY_EDU_FORM);
    setShowForm(false);
  };

  const submit = async () => {
    if (!canSend || busy) return;
    setBusy(true);
    try {
      await onSubmitEducation?.({ ...form, name: form.name.trim() });
      resetForm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <View style={{ flex: 1 }}>
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ padding: 16, paddingBottom: 32 }}
      {...THIN_SCROLLBAR_PROPS}
      keyboardShouldPersistTaps="handled"
    >
      <SectionSearchBar
        isDarkMode={isDarkMode}
        styles={styles}
        value={venueQuery}
        onChangeText={setVenueQuery}
        placeholder="Search venues by name, city, or state"
      />
      <TouchableOpacity
        style={[styles.travelChip, trainingOnly && { backgroundColor: '#27ae60', borderColor: '#27ae60' }]}
        onPress={() => setTrainingOnly((value) => !value)}
        accessibilityState={{ selected: trainingOnly }}
      >
        <Text style={[styles.travelChipText, trainingOnly && styles.travelChipTextActive]}>
          Show venues that offer consistent training to their employees
        </Text>
      </TouchableOpacity>

      <View style={styles.resourceResultsHeader}>
        <Text style={styles.resourceResultsTitle}>
          {trainingOnly ? `${visibleVenues.length} with training` : 'Learn'}
        </Text>
        <TouchableOpacity onPress={onDeselectAll} disabled={!selectedIds.length}>
          <Text style={[styles.deselectLink, !selectedIds.length && styles.deselectLinkDisabled]}>
            Deselect All
          </Text>
        </TouchableOpacity>
      </View>

      {visibleVenues.length === 0 ? (
        <Text style={styles.resourceEmptyText}>
          {normalizedVenueQuery
            ? 'No matching venues.'
            : trainingOnly
              ? 'No venues tagged for consistent training yet.'
              : 'Add an educational location below.'}
        </Text>
      ) : visibleVenues.map((venue) => {
        const selected = selectedSet.has(String(venue.id));
        return (
          <TouchableOpacity
            key={venue.id}
            style={[styles.resourceResultItem, selected && styles.resourceResultItemSelected]}
            onPress={() => onToggleVenue?.(venue)}
          >
            <View style={styles.resourceResultCopy}>
              <Text style={styles.resourceResultName} numberOfLines={1}>{venue.name || 'Venue'}</Text>
              <Text style={styles.resourceResultDescription} numberOfLines={1}>
                {[venue.city, venue.state].filter(Boolean).join(', ') || 'Location'}
              </Text>
            </View>
          </TouchableOpacity>
        );
      })}

      <TouchableOpacity
        style={[styles.resourceSubmitButton, { marginTop: 16 }]}
        onPress={() => { if (showForm || requireContributor()) setShowForm((value) => !value); }}
      >
        <Text style={styles.resourceSubmitButtonText}>
          {showForm ? 'CLOSE FORM' : 'ADD EDUCATIONAL LOCATION'}
        </Text>
      </TouchableOpacity>

      {showForm ? (
        <View style={{ marginTop: 14 }}>
          <Text style={styles.resourceFormHelpText}>
            Questions stay in this tab. Admins review before the location appears on the map.
          </Text>
          {[
            { key: 'name', label: 'Location Name', placeholder: 'Workshop, school, or studio' },
            { key: 'locationType', label: 'Location Type', placeholder: 'School, workshop, residency…' },
            { key: 'resourcesProvided', label: 'Educational Resources Provided', placeholder: 'What is offered here?', multiline: true },
            { key: 'website', label: 'Website', placeholder: 'https://' },
          ].map((field) => (
            <View key={field.key} style={{ marginBottom: 10 }}>
              <Text style={styles.sideModalFieldLabel}>{field.label}</Text>
              <TextInput
                style={[styles.sideModalInput, field.multiline && styles.sideModalTextArea]}
                value={form[field.key]}
                onChangeText={(value) => setForm((prev) => ({ ...prev, [field.key]: value }))}
                placeholder={field.placeholder}
                placeholderTextColor={placeholderColor}
                multiline={Boolean(field.multiline)}
              />
            </View>
          ))}
          <MapEmbedField
            styles={styles}
            placeholderColor={placeholderColor}
            value={form.mapEmbedLink}
            onChangeText={(mapEmbedLink) => setForm((prev) => ({ ...prev, mapEmbedLink }))}
            embedStatus={embedStatus}
          />
          <TouchableOpacity
            style={[styles.resourceSubmitButton, (!canSend || busy) && styles.sideModalButtonDisabled]}
            onPress={submit}
            disabled={!canSend || busy}
          >
            <Text style={styles.resourceSubmitButtonText}>{busy ? 'SENDING…' : 'SEND'}</Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </ScrollView>
    <RouteRadiusControl styles={styles} routeRadius={routeRadius} section="learn" />
    </View>
  );
}

function EventFlyersPanel({
  isDarkMode,
  styles,
  venueOptions = [],
  items = [],
  selectedIds = [],
  onToggleItem,
  onSubmitFlyer,
  onEmbedLocationPreview,
  onClearEmbedLocationPreview,
  routeRadius = null,
  openRequest = null,
  onOpenRequestHandled,
}) {
  const requireContributor = useMapContributorGate();
  const [showForm, setShowForm] = useState(false);
  const [flyerQuery, setFlyerQuery] = useState('');

  // Opened programmatically from the "Add to Map" chooser (REQ-065).
  useEffect(() => {
    if (openRequest?.action !== 'add') return;
    setShowForm(true);
    onOpenRequestHandled?.();
  }, [openRequest, onOpenRequestHandled]);
  const inRouteRadius = useRouteRadiusFilter(routeRadius, 'flyers');
  const normalizedFlyerQuery = flyerQuery.trim().toLowerCase();
  const visibleFlyers = items
    .filter((item) => !normalizedFlyerQuery || item.searchText?.includes(normalizedFlyerQuery))
    .filter(inRouteRadius);
  const selectedFlyerSet = new Set(selectedIds);
  useRoutePreviewPins(
    routeRadius,
    'flyers',
    normalizedFlyerQuery
      ? visibleFlyers.map((item) => toPreviewPin(item.id, item.name, item)).filter(Boolean)
      : [],
  );
  const [form, setForm] = useState(EMPTY_FLYER_FORM);
  const [venueQuery, setVenueQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const placeholderColor = isDarkMode ? 'rgba(255,255,255,0.36)' : 'rgba(0,0,0,0.36)';
  const matches = venueQuery.trim()
    ? venueOptions.filter((venue) =>
      String(venue.name || '').toLowerCase().includes(venueQuery.trim().toLowerCase())
    ).slice(0, 8)
    : [];
  const canPost = Boolean(form.title.trim());

  const resetForm = () => {
    onClearEmbedLocationPreview?.();
    setForm(EMPTY_FLYER_FORM);
    setVenueQuery('');
    setShowForm(false);
  };

  const submit = async () => {
    if (!canPost || busy) return;
    setBusy(true);
    try {
      await onSubmitFlyer?.({ ...form, title: form.title.trim() });
      resetForm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <View style={{ flex: 1 }}>
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ padding: 16, paddingBottom: 32 }}
      {...THIN_SCROLLBAR_PROPS}
      keyboardShouldPersistTaps="handled"
    >
      <Text style={styles.sideModalTitle}>Event Flyers</Text>
      <Text style={styles.resourceFormHelpText}>
        Search results show as a box on the map. Click a poster to open the event page.
        This will be posted to the Event Flyers area of the website.
      </Text>
      <SectionSearchBar
        isDarkMode={isDarkMode}
        styles={styles}
        value={flyerQuery}
        onChangeText={setFlyerQuery}
        placeholder="Search flyers..."
      />
      <View style={styles.resourceResultsHeader}>
        <Text style={styles.resourceResultsTitle} numberOfLines={1}>
          {`${visibleFlyers.length} ${visibleFlyers.length === 1 ? 'flyer' : 'flyers'}`}
        </Text>
      </View>
      {visibleFlyers.length === 0 ? (
        <Text style={[styles.resourceEmptyText, { marginBottom: 12 }]}>
          {normalizedFlyerQuery ? 'No matching flyers.' : 'Approved event flyers will appear here.'}
        </Text>
      ) : visibleFlyers.map((item) => {
        const selected = selectedFlyerSet.has(item.id);
        return (
          <TouchableOpacity
            key={item.id}
            style={[styles.resourceResultItem, selected && styles.resourceResultItemSelected]}
            onPress={() => onToggleItem?.(item)}
            accessibilityState={{ selected }}
          >
            <View style={styles.resourceResultCopy}>
              <Text style={styles.resourceResultName} numberOfLines={1}>{item.name}</Text>
              <Text style={styles.resourceResultDescription} numberOfLines={2}>
                {item.description || item.location || 'Event flyer'}
              </Text>
            </View>
            <View style={[styles.resourceSelectMark, selected && styles.resourceSelectMarkSelected]}>
              {selected ? <Ionicons name="checkmark" size={14} color="#ffffff" /> : null}
            </View>
          </TouchableOpacity>
        );
      })}
      <TouchableOpacity
        style={[styles.resourceSubmitButton, { marginTop: 12 }]}
        onPress={() => { if (showForm || requireContributor()) setShowForm((value) => !value); }}
      >
        <Text style={styles.resourceSubmitButtonText}>
          {showForm ? 'CLOSE TEMPLATE' : 'POST YOURS!'}
        </Text>
      </TouchableOpacity>

      {showForm ? (
        <View style={{ marginTop: 14 }}>
          {[
            { key: 'title', label: 'TITLE', placeholder: 'Show title' },
            { key: 'date', label: 'DATE', placeholder: 'YYYY-MM-DD' },
            { key: 'link', label: 'LINK', placeholder: 'https://' },
            { key: 'ticketLink', label: 'ticket link', placeholder: 'https://tickets' },
            { key: 'organizedBy', label: 'organized by', placeholder: 'Collective or promoter' },
            { key: 'details', label: 'EVENT DETAILS', placeholder: 'Age, price, notes…', multiline: true },
          ].map((field) => (
            <View key={field.key} style={{ marginBottom: 10 }}>
              <Text style={styles.sideModalFieldLabel}>{field.label}</Text>
              <TextInput
                style={[styles.sideModalInput, field.multiline && styles.sideModalTextArea]}
                value={form[field.key]}
                onChangeText={(value) => setForm((prev) => ({ ...prev, [field.key]: value }))}
                placeholder={field.placeholder}
                placeholderTextColor={placeholderColor}
                multiline={Boolean(field.multiline)}
              />
            </View>
          ))}
          <Text style={styles.sideModalFieldLabel}>VENUE</Text>
          <TextInput
            style={styles.sideModalInput}
            value={venueQuery || form.venueName}
            onChangeText={(value) => {
              setVenueQuery(value);
              setForm((prev) => ({ ...prev, venueName: value, venueId: '' }));
            }}
            placeholder="Search the venue database"
            placeholderTextColor={placeholderColor}
          />
          {matches.map((venue) => (
            <TouchableOpacity
              key={venue.id}
              style={styles.resourceResultItem}
              onPress={() => {
                setForm((prev) => ({ ...prev, venueId: String(venue.id), venueName: venue.name || '' }));
                setVenueQuery(venue.name || '');
              }}
            >
              <Text style={styles.resourceResultName}>{venue.name}</Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity
            style={[styles.resourceSubmitButton, { marginTop: 12 }, (!canPost || busy) && styles.sideModalButtonDisabled]}
            onPress={submit}
            disabled={!canPost || busy}
          >
            <Text style={styles.resourceSubmitButtonText}>{busy ? 'POSTING…' : 'POST'}</Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </ScrollView>
    <RouteRadiusControl styles={styles} routeRadius={routeRadius} section="flyers" />
    </View>
  );
}

export default function MapSideRail({
  isDarkMode = true,
  isMobile = false,
  side = 'left',
  venueCriteria = null,
  resourceDirectory = null,
  travel = null,
  food = null,
  directory = null,
  learn = null,
  flyers = null,
  onWidthChange = null,
  onSubmitVenue = null,
  onEmbedLocationPreview = null,
  onClearEmbedLocationPreview = null,
  fullscreenHostRef = null,
  openRequest = null,
  onOpenRequestHandled = null,
  /** Calendar MAP tab only: { lineCoords, radii, onChangeRadius, onPreviewPins } — see useRouteRadiusFilter. */
  routeRadius = null,
  /** Called with the open section's item id (or null) so the map can draw that section's radius. */
  onSectionChange = null,
}) {
  const [hovered, setHovered] = useState(false);
  const [hoveredItemId, setHoveredItemId] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  // Requests raised from inside the rail (the Add to Map chooser) behave the
  // same as ones passed in from TourPlanner.
  const [internalRequest, setInternalRequest] = useState(null);
  const activeRequest = internalRequest || openRequest;

  useEffect(() => {
    if (!openRequest?.itemId) return;
    setSelectedItemId(openRequest.itemId);
  }, [openRequest]);

  const navigateToSection = (itemId, action = null) => {
    setSelectedItemId(itemId);
    setInternalRequest(action ? { itemId, action } : null);
  };

  const clearInternalRequest = () => setInternalRequest(null);

  // The venue form is opt-in from the chooser; reset it whenever the rail moves
  // away from "Add to Map" so it never reappears unexpectedly.
  const [showVenueForm, setShowVenueForm] = useState(false);
  useEffect(() => {
    if (selectedItemId !== 'plus') setShowVenueForm(false);
  }, [selectedItemId]);

  const selectedItem = MAP_SIDE_RAIL_ITEMS.find((item) => item.id === selectedItemId) || null;
  // Desktop: labels expand on hover or while a section is open. Mobile: skip labels — icon opens the panel directly.
  const labelsOpen = !isMobile && (Boolean(selectedItem) || (Platform.OS === 'web' && hovered));
  const styles = makeStyles(isDarkMode, side);

  const selectItem = (itemId) => {
    // Tap same icon again to collapse (especially useful on mobile).
    setSelectedItemId((prev) => (prev === itemId ? null : itemId));
  };

  const collapseAll = () => {
    onClearEmbedLocationPreview?.();
    setSelectedItemId(null);
    setHovered(false);
    setHoveredItemId(null);
  };

  const iconColWidth = isMobile ? MAP_SIDE_RAIL_MOBILE_WIDTH : MAP_SIDE_RAIL_WIDTH;
  const panelWidth = isMobile ? Math.min(MAP_SIDE_PANEL_WIDTH, 280) : MAP_SIDE_PANEL_WIDTH;
  const wrapperWidth =
    iconColWidth +
    (labelsOpen ? MAP_SIDE_LABELS_WIDTH : 0) +
    (selectedItem ? panelWidth : 0);

  useEffect(() => {
    onWidthChange?.(wrapperWidth);
  }, [wrapperWidth]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onClearEmbedLocationPreview?.();
    onSectionChange?.(selectedItemId);
  }, [selectedItemId]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderPanelBody = () => {
    if (!selectedItem) return null;
    if (selectedItem.id === 'map' && venueCriteria) {
      return (
        <VenueCriteriaPanel
          isDarkMode={isDarkMode}
          styles={styles}
          filters={venueCriteria.filters}
          onToggleAffiliate={venueCriteria.onToggleAffiliate}
          onToggleFeature={venueCriteria.onToggleFeature}
          affiliateChipColor={venueCriteria.affiliateChipColor}
          onDeselectAll={venueCriteria.onDeselectAll}
          searchQuery={venueCriteria.searchQuery}
          onSearchChange={venueCriteria.onSearchChange}
          routeRadius={routeRadius}
        />
      );
    }
    if (selectedItem.id === 'leaf' && resourceDirectory) {
      return (
        <ResourcesPanel
          isDarkMode={isDarkMode}
          styles={styles}
          items={resourceDirectory.items}
          categories={resourceDirectory.categories}
          selectedIds={resourceDirectory.selectedIds}
          loadingIds={resourceDirectory.loadingIds}
          venueOptions={resourceDirectory.venueOptions}
          onToggleItem={resourceDirectory.onToggleItem}
          onDeselectAll={resourceDirectory.onDeselectAll}
          onKeepSelectedIds={resourceDirectory.onKeepSelectedIds}
          onSubmitResource={resourceDirectory.onSubmitResource}
          onSubmitMerch={resourceDirectory.onSubmitMerch}
          onEmbedLocationPreview={onEmbedLocationPreview}
          onClearEmbedLocationPreview={onClearEmbedLocationPreview}
          fullscreenHostRef={fullscreenHostRef}
          openRequest={activeRequest?.itemId === 'leaf' ? activeRequest : null}
          onOpenRequestHandled={() => { clearInternalRequest(); onOpenRequestHandled?.(); }}
          routeRadius={routeRadius}
        />
      );
    }
    if (selectedItem.id === 'plane' && travel) {
      return (
        <TravelPanel
          isDarkMode={isDarkMode}
          styles={styles}
          {...travel}
          onEmbedLocationPreview={onEmbedLocationPreview}
          onClearEmbedLocationPreview={onClearEmbedLocationPreview}
          fullscreenHostRef={fullscreenHostRef}
          openRequest={activeRequest?.itemId === 'plane' ? activeRequest : null}
          onOpenRequestHandled={() => { clearInternalRequest(); onOpenRequestHandled?.(); }}
          routeRadius={routeRadius}
        />
      );
    }
    if (selectedItem.id === 'food') {
      return (
        <FoodPanel
          isDarkMode={isDarkMode}
          styles={styles}
          {...(food || {})}
          onEmbedLocationPreview={onEmbedLocationPreview}
          onClearEmbedLocationPreview={onClearEmbedLocationPreview}
          fullscreenHostRef={fullscreenHostRef}
          openRequest={activeRequest?.itemId === 'food' ? activeRequest : null}
          onOpenRequestHandled={() => { clearInternalRequest(); onOpenRequestHandled?.(); }}
        />
      );
    }
    if (selectedItem.id === 'directory') {
      return (
        <DirectoryPanel
          isDarkMode={isDarkMode}
          styles={styles}
          {...(directory || {})}
          routeRadius={routeRadius}
        />
      );
    }
    if (selectedItem.id === 'school') {
      return (
        <LearnPanel
          isDarkMode={isDarkMode}
          styles={styles}
          {...(learn || {})}
          onEmbedLocationPreview={onEmbedLocationPreview}
          onClearEmbedLocationPreview={onClearEmbedLocationPreview}
          routeRadius={routeRadius}
        />
      );
    }
    if (selectedItem.id === 'poster') {
      return (
        <EventFlyersPanel
          isDarkMode={isDarkMode}
          styles={styles}
          {...(flyers || {})}
          onEmbedLocationPreview={onEmbedLocationPreview}
          onClearEmbedLocationPreview={onClearEmbedLocationPreview}
          routeRadius={routeRadius}
          openRequest={activeRequest?.itemId === 'poster' ? activeRequest : null}
          onOpenRequestHandled={() => { clearInternalRequest(); onOpenRequestHandled?.(); }}
        />
      );
    }
    if (selectedItem.id === 'person') {
      return (
        <SavedContactsPanel
          isDarkMode={isDarkMode}
          styles={styles}
        />
      );
    }
    if (selectedItem.id === 'plus') {
      if (showVenueForm) {
        return (
          <SuggestVenuePanel
            isDarkMode={isDarkMode}
            styles={styles}
            onSubmitVenue={onSubmitVenue}
            onEmbedLocationPreview={onEmbedLocationPreview}
            onClearEmbedLocationPreview={onClearEmbedLocationPreview}
          />
        );
      }
      return (
        <AddToMapPanel
          isDarkMode={isDarkMode}
          styles={styles}
          onNavigate={navigateToSection}
        />
      );
    }
    return (
      <Text style={styles.placeholderText}>
        {selectedItem.label} content will be added here.
      </Text>
    );
  };

  return (
    <View
      style={[
        styles.wrapper,
        { width: wrapperWidth },
        isMobile && styles.wrapperMobile,
      ]}
      onMouseEnter={() => {
        if (!isMobile) setHovered(true);
      }}
      onMouseLeave={() => {
        // Don't collapse labels while a section panel is open.
        if (selectedItem) {
          setHoveredItemId(null);
          return;
        }
        setHovered(false);
        setHoveredItemId(null);
      }}
      pointerEvents="auto"
    >
      {/* Column 1: icons */}
      <View style={[styles.iconsCol, isMobile && styles.iconsColMobile]}>
        <View style={styles.inner}>
          {MAP_SIDE_RAIL_ITEMS.map((item) => {
            const selected = selectedItemId === item.id;
            const itemHovered = hoveredItemId === item.id;
            return (
              <TouchableOpacity
                key={item.id}
                style={[
                  styles.iconBtn,
                  itemHovered && styles.itemHovered,
                  selected && styles.itemSelected,
                ]}
                onPress={() => selectItem(item.id)}
                onMouseEnter={() => {
                  if (!isMobile) setHoveredItemId(item.id);
                }}
                onMouseLeave={() => {
                  if (!isMobile) setHoveredItemId(null);
                }}
                accessibilityLabel={item.label}
                accessibilityRole="button"
                accessibilityState={{ expanded: selected }}
                activeOpacity={0.75}
              >
                <Ionicons
                  name={item.icon}
                  size={isMobile ? 16 : 17}
                  color={selected
                    ? '#ffffff'
                    : (isDarkMode ? 'rgba(255,255,255,0.78)' : 'rgba(0,0,0,0.62)')}
                />
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      {/* Column 2: section names — desktop only */}
      {labelsOpen ? (
        <View style={styles.labelsCol}>
          <View style={styles.inner}>
            {MAP_SIDE_RAIL_ITEMS.map((item) => {
              const selected = selectedItemId === item.id;
              const itemHovered = hoveredItemId === item.id;
              return (
                <TouchableOpacity
                  key={item.id}
                  style={[
                    styles.labelBtn,
                    itemHovered && styles.itemHovered,
                    selected && styles.itemSelected,
                  ]}
                  onPress={() => selectItem(item.id)}
                  onMouseEnter={() => setHoveredItemId(item.id)}
                  onMouseLeave={() => setHoveredItemId(null)}
                  accessibilityLabel={item.label}
                  accessibilityRole="button"
                  accessibilityState={{ expanded: selected }}
                  activeOpacity={0.75}
                >
                  <Text
                    style={[styles.label, selected && styles.labelSelected]}
                    numberOfLines={1}
                  >
                    {item.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      ) : null}

      {/* Column 3: section contents */}
      {selectedItem ? (
        <View style={[styles.panel, isMobile && styles.panelMobile]}>
          <View style={styles.panelHeader}>
            <View style={styles.panelTitleRow}>
              <Ionicons
                name={selectedItem.icon}
                size={isMobile ? 17 : 19}
                color={isDarkMode ? '#ffffff' : '#1a1a1a'}
              />
              <Text style={[styles.panelTitle, isMobile && styles.panelTitleMobile]} numberOfLines={1}>
                {selectedItem.label}
              </Text>
            </View>
            <TouchableOpacity
              onPress={collapseAll}
              accessibilityLabel="Collapse side menu"
              accessibilityRole="button"
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              style={styles.panelHeaderClose}
            >
              <Text style={styles.panelHeaderCloseText}>✕</Text>
            </TouchableOpacity>
          </View>

          {renderPanelBody()}

          {/* Collapse control — center of right edge (desktop + mobile) */}
          <TouchableOpacity
            style={[styles.collapseTab, isMobile && styles.collapseTabMobile]}
            onPress={collapseAll}
            accessibilityLabel="Collapse side menu"
            accessibilityRole="button"
            activeOpacity={0.8}
          >
            <Ionicons
              name={side === 'right' ? 'chevron-forward' : 'chevron-back'}
              size={isMobile ? 18 : 16}
              color={isDarkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.65)'}
            />
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(isDarkMode, side) {
  const isRight = side === 'right';
  const chromeBg = isDarkMode ? 'rgba(10, 12, 20, 0.78)' : 'rgba(255,255,255,0.88)';
  const panelBg = isDarkMode ? 'rgba(15,18,28,0.96)' : 'rgba(255,255,255,0.97)';
  const borderColor = isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)';

  return StyleSheet.create({
    wrapper: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      ...(isRight ? { right: 0 } : { left: 0 }),
      zIndex: 22,
      flexDirection: isRight ? 'row-reverse' : 'row',
      alignItems: 'stretch',
      ...(Platform.OS === 'web' ? {
        transition: 'width 140ms ease',
      } : {}),
    },
    wrapperMobile: {
      maxWidth: '100%',
    },
    iconsCol: {
      width: MAP_SIDE_RAIL_WIDTH,
      flexShrink: 0,
      paddingVertical: 8,
      backgroundColor: chromeBg,
      borderRightWidth: isRight ? 0 : StyleSheet.hairlineWidth,
      borderLeftWidth: isRight ? StyleSheet.hairlineWidth : 0,
      borderColor,
      ...(Platform.OS === 'web' ? {
        backdropFilter: 'blur(16px) saturate(150%)',
        WebkitBackdropFilter: 'blur(16px) saturate(150%)',
      } : {}),
    },
    iconsColMobile: {
      width: MAP_SIDE_RAIL_MOBILE_WIDTH,
      paddingVertical: 6,
    },
    labelsCol: {
      width: MAP_SIDE_LABELS_WIDTH,
      flexShrink: 0,
      paddingVertical: 8,
      paddingRight: 8,
      backgroundColor: chromeBg,
      borderRightWidth: StyleSheet.hairlineWidth,
      borderColor,
      ...(Platform.OS === 'web' ? {
        backdropFilter: 'blur(16px) saturate(150%)',
        WebkitBackdropFilter: 'blur(16px) saturate(150%)',
      } : {}),
    },
    inner: {
      justifyContent: 'center',
      alignItems: 'stretch',
      gap: 4,
    },
    iconBtn: {
      height: 32,
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 6,
    },
    labelBtn: {
      height: 32,
      paddingHorizontal: 10,
      justifyContent: 'center',
      borderRadius: 6,
    },
    itemHovered: {
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    },
    itemSelected: {
      backgroundColor: 'rgba(230,126,34,0.92)',
    },
    label: {
      fontSize: 12,
      fontWeight: '600',
      letterSpacing: 0.1,
      color: isDarkMode ? 'rgba(255,255,255,0.86)' : 'rgba(0,0,0,0.72)',
    },
    labelSelected: {
      color: '#ffffff',
    },
    panel: {
      width: MAP_SIDE_PANEL_WIDTH,
      flexShrink: 0,
      padding: 18,
      backgroundColor: panelBg,
      borderRightWidth: StyleSheet.hairlineWidth,
      borderColor,
      position: 'relative',
      overflow: 'visible',
      flexDirection: 'column',
    },
    panelMobile: {
      width: 280,
      maxWidth: 'calc(100vw - 44px)',
      padding: 14,
    },
    panelHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      paddingBottom: 14,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)',
      marginBottom: 12,
      flexShrink: 0,
    },
    panelTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 9,
      flex: 1,
      minWidth: 0,
    },
    panelTitle: {
      fontSize: 16,
      fontWeight: '700',
      color: isDarkMode ? '#ffffff' : '#1a1a1a',
    },
    panelTitleMobile: {
      fontSize: 15,
    },
    panelHeaderClose: {
      padding: 4,
      flexShrink: 0,
    },
    panelHeaderCloseText: {
      fontSize: 18,
      lineHeight: 20,
      fontWeight: '600',
      color: isDarkMode ? 'rgba(255,255,255,0.62)' : 'rgba(0,0,0,0.52)',
    },
    placeholderText: {
      marginTop: 4,
      fontSize: 13,
      lineHeight: 19,
      color: isDarkMode ? 'rgba(255,255,255,0.52)' : 'rgba(0,0,0,0.50)',
    },
    collapseTab: {
      position: 'absolute',
      top: '50%',
      right: 0,
      marginTop: -18,
      width: 16,
      height: 36,
      borderTopLeftRadius: 8,
      borderBottomLeftRadius: 8,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: isDarkMode ? 'rgba(25, 28, 40, 0.95)' : 'rgba(245,245,245,0.98)',
      borderWidth: StyleSheet.hairlineWidth,
      borderColor,
      borderRightWidth: 0,
      zIndex: 20,
      ...(Platform.OS === 'web' ? {
        cursor: 'pointer',
        position: 'absolute',
        transform: 'translateY(0)',
        pointerEvents: 'auto',
      } : {}),
    },
    collapseTabMobile: {
      width: 22,
      height: 44,
      marginTop: -22,
      borderTopLeftRadius: 10,
      borderBottomLeftRadius: 10,
    },

    // Venue Criteria — mirrors TourPlanner filter chips / category pills
    criteriaRoot: {
      flex: 1,
      minHeight: 0,
    },
    criteriaTopRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      marginBottom: 10,
    },
    criteriaTabsRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 5,
      flex: 1,
    },
    deselectLink: {
      fontSize: 11,
      fontWeight: '600',
      color: isDarkMode ? 'rgba(120,210,255,0.95)' : '#2586b0',
      textDecorationLine: 'underline',
      flexShrink: 0,
    },
    deselectLinkDisabled: {
      opacity: 0.35,
      textDecorationLine: 'none',
    },
    criteriaScroll: {
      flex: 1,
      minHeight: 0,
      ...(Platform.OS === 'web' ? {
        scrollbarWidth: 'thin',
        scrollbarColor: isDarkMode
          ? 'rgba(255,255,255,0.28) transparent'
          : 'rgba(128,128,128,0.4) transparent',
      } : {}),
    },
    criteriaScrollContent: {
      paddingBottom: 20,
    },
    venueFilterRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 5,
      width: '100%',
    },
    venueFilterChip: {
      paddingVertical: 4,
      paddingHorizontal: 9,
      borderRadius: 5,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
      borderWidth: 1.5,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.22)',
    },
    venueFilterChipLink: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: 5,
      paddingVertical: 5,
      paddingHorizontal: 10,
    },
    venueFilterChipActive: {
      backgroundColor: isDarkMode ? 'rgba(120,210,255,0.55)' : 'rgba(37,150,190,0.5)',
      borderColor: isDarkMode ? 'rgba(120,210,255,0.75)' : 'rgba(37,150,190,0.4)',
    },
    venueFilterChipText: {
      color: isDarkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.65)',
      fontSize: 11,
      fontWeight: '500',
    },
    venueFilterChipTextActive: {
      color: isDarkMode ? '#062a38' : '#fff',
      fontWeight: '600',
    },
    categoryPill: {
      paddingVertical: 5,
      paddingHorizontal: 10,
      borderRadius: 6,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
      borderWidth: 1.5,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.16)',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    categoryPillOpen: {
      backgroundColor: isDarkMode ? 'rgba(120,210,255,0.55)' : 'rgba(37,150,190,0.18)',
      borderColor: isDarkMode ? 'rgba(120,210,255,0.75)' : 'rgba(37,150,190,0.55)',
    },
    categoryPillHasFilter: {
      borderColor: 'rgba(230,126,34,0.6)',
    },
    categoryPillText: {
      color: isDarkMode ? 'rgba(255,255,255,0.82)' : 'rgba(0,0,0,0.68)',
      fontSize: 11,
      fontWeight: '600',
      letterSpacing: 0.1,
    },
    categoryPillTextOpen: {
      color: isDarkMode ? '#062a38' : '#0a4a6e',
      fontWeight: '700',
    },
    categoryPillBadge: {
      fontSize: 9,
      fontWeight: '700',
      color: isDarkMode ? '#062a38' : '#e67e22',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(230,126,34,0.18)',
      paddingHorizontal: 4,
      paddingVertical: 1,
      borderRadius: 8,
      overflow: 'hidden',
    },
    farmDayCard: {
      width: 148,
      minHeight: 88,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#fffffe',
      justifyContent: 'center',
      gap: 3,
      flexShrink: 0,
    },
    farmDayCardSelected: {
      backgroundColor: isDarkMode ? 'rgba(232,164,61,0.22)' : 'rgba(232,164,61,0.16)',
      borderColor: isDarkMode ? 'rgba(232,164,61,0.7)' : 'rgba(232,164,61,0.55)',
    },
    farmDayCardTop: {
      flexDirection: 'row',
      alignItems: 'baseline',
      gap: 6,
    },
    farmDayDow: {
      fontFamily: FONT.mono,
      fontSize: 10.5,
      letterSpacing: 0.8,
      textTransform: 'uppercase',
      color: isDarkMode ? 'rgba(255,255,255,0.62)' : 'rgba(0,0,0,0.5)',
    },
    farmDayNum: {
      fontFamily: FONT.sans,
      fontSize: 16.5,
      fontWeight: '400',
      letterSpacing: -0.3,
      lineHeight: 20,
      color: isDarkMode ? '#f4f4f2' : '#14141a',
    },
    farmDayName: {
      fontFamily: FONT.sans,
      fontSize: 13,
      fontWeight: '400',
      color: isDarkMode ? 'rgba(255,255,255,0.88)' : '#14141a',
    },
    farmDayMeta: {
      fontFamily: FONT.mono,
      fontSize: 10,
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      color: isDarkMode ? 'rgba(255,255,255,0.46)' : 'rgba(0,0,0,0.42)',
    },
    farmDayTextSelected: {
      color: isDarkMode ? '#fff6e6' : '#3a2508',
    },
    farmDayMetaSelected: {
      color: isDarkMode ? 'rgba(255,246,230,0.72)' : 'rgba(58,37,8,0.62)',
    },
    hintText: {
      fontSize: 9.5,
      color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
      marginTop: 8,
      lineHeight: 14,
    },

    // Calendar MAP tab: search radius pinned to the bottom of each section
    routeRadiusWrap: {
      flexShrink: 0,
      gap: 6,
      paddingHorizontal: 16,
      paddingTop: 10,
      paddingBottom: 12,
      borderTopWidth: 1,
      borderTopColor: borderColor,
    },
    routeRadiusHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    routeRadiusValue: {
      fontSize: 12,
      fontWeight: '700',
    },
    routeRadiusHint: {
      fontSize: 10.5,
      color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
    },

    // Travel — POI, transit, and map legend controls
    travelRoot: {
      flex: 1,
      minHeight: 0,
      ...(Platform.OS === 'web' ? {
        scrollbarWidth: 'thin',
        scrollbarColor: isDarkMode
          ? 'rgba(255,255,255,0.28) transparent'
          : 'rgba(128,128,128,0.4) transparent',
      } : {}),
    },
    travelContent: {
      paddingBottom: 22,
    },
    travelLoadingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
      paddingBottom: 8,
    },
    travelLoadingText: {
      fontSize: 10.5,
      color: isDarkMode ? 'rgba(255,255,255,0.52)' : 'rgba(0,0,0,0.50)',
    },
    travelSection: {
      paddingVertical: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.09)',
    },
    travelSectionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
      marginBottom: 9,
    },
    travelSectionTitle: {
      flex: 1,
      minWidth: 0,
      fontSize: 12,
      fontWeight: '700',
      color: isDarkMode ? 'rgba(255,255,255,0.90)' : 'rgba(0,0,0,0.78)',
    },
    travelChipRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 6,
    },
    travelChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingVertical: 7,
      paddingHorizontal: 10,
      borderRadius: 5,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.12)',
    },
    travelChipText: {
      fontSize: 10.5,
      fontWeight: '600',
      color: isDarkMode ? 'rgba(255,255,255,0.80)' : 'rgba(0,0,0,0.70)',
    },
    travelChipTextActive: {
      color: '#ffffff',
      fontWeight: '700',
    },
    travelTransitChipActive: {
      backgroundColor: isDarkMode ? 'rgba(120,210,255,0.65)' : 'rgba(37,150,190,0.88)',
      borderColor: isDarkMode ? 'rgba(120,210,255,0.82)' : 'rgba(37,150,190,0.95)',
    },
    travelTransitChipTextActive: {
      color: isDarkMode ? '#062a38' : '#ffffff',
      fontWeight: '700',
    },
    travelChipEmoji: {
      fontSize: 11,
    },
    travelLegend: {
      paddingTop: 14,
    },
    travelLegendTitle: {
      marginBottom: 9,
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      color: isDarkMode ? 'rgba(255,255,255,0.58)' : 'rgba(0,0,0,0.52)',
    },
    travelLegendItems: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
    },
    travelLegendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    travelLegendImage: {
      width: 13,
      height: 13,
    },
    travelLegendText: {
      fontSize: 10,
      fontWeight: '500',
      color: isDarkMode ? 'rgba(255,255,255,0.58)' : 'rgba(0,0,0,0.52)',
    },

    // Resources search + selectable location list
    resourcesRoot: {
      flex: 1,
      minHeight: 0,
    },
    resourceSearchRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
    },
    resourceSearchBox: {
      flex: 1,
      minHeight: 40,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingLeft: 14,
      paddingRight: 12,
      paddingVertical: 8,
      borderRadius: 7,
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.14)',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
    },
    resourceSearchInput: {
      flex: 1,
      minWidth: 0,
      paddingVertical: 0,
      paddingHorizontal: 0,
      borderWidth: 0,
      outlineStyle: 'none',
      backgroundColor: 'transparent',
      fontSize: 12,
      color: isDarkMode ? '#ffffff' : '#1a1a1a',
    },
    resourceAddButton: {
      width: 38,
      height: 38,
      borderRadius: 7,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#e67e22',
    },
    textToggleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 22,
      marginTop: 4,
      marginBottom: 2,
    },
    foodToggleWrap: {
      marginTop: 14,
    },
    foodLinksWrap: {
      marginTop: 12,
      gap: 8,
    },
    foodLinksRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
    },
    foodLinkChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      maxWidth: '100%',
      paddingVertical: 5,
      paddingHorizontal: 9,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.14)',
    },
    foodLinkChipText: {
      fontSize: 11,
      fontWeight: '600',
      color: isDarkMode ? 'rgba(255,255,255,0.86)' : 'rgba(0,0,0,0.78)',
    },
    textToggleLabel: {
      fontSize: 13,
      fontWeight: '600',
      letterSpacing: 0.2,
      color: isDarkMode ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.42)',
    },
    textToggleLabelActive: {
      color: '#e67e22',
      textDecorationLine: 'underline',
    },
    directoryResultsContent: {
      alignItems: 'center',
      paddingTop: 2,
      paddingBottom: 20,
      gap: 12,
    },
    directoryCardWrap: {
      width: '100%',
      maxWidth: 248,
      alignSelf: 'center',
      borderRadius: 10,
      borderWidth: 2,
      borderColor: 'transparent',
      overflow: 'hidden',
    },
    directoryCardWrapSelected: {
      borderColor: '#e67e22',
    },
    directoryLoading: {
      marginTop: 24,
    },
    foodItemTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
    },
    foodItemAddButton: {
      paddingVertical: 3,
      paddingHorizontal: 7,
      borderRadius: 4,
      backgroundColor: '#e67e22',
    },
    foodItemAddButtonText: {
      fontSize: 9,
      fontWeight: '800',
      letterSpacing: 0.4,
      color: '#ffffff',
    },
    resourceResultsHeader: {
      marginTop: 14,
      marginBottom: 8,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
    },
    resourceResultsTitle: {
      flex: 1,
      minWidth: 0,
      fontSize: 12,
      fontWeight: '700',
      color: isDarkMode ? 'rgba(255,255,255,0.88)' : 'rgba(0,0,0,0.76)',
    },
    resourceResultsScroll: {
      flex: 1,
      minHeight: 0,
      ...(Platform.OS === 'web' ? {
        scrollbarWidth: 'thin',
        scrollbarColor: isDarkMode
          ? 'rgba(255,255,255,0.28) transparent'
          : 'rgba(128,128,128,0.4) transparent',
      } : {}),
    },
    resourceResultsContent: {
      paddingBottom: 20,
      gap: 7,
    },
    resourceResultItem: {
      minHeight: 64,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 9,
      padding: 8,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.10)',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.025)',
    },
    resourceResultItemSelected: {
      borderColor: 'rgba(230,126,34,0.78)',
      backgroundColor: 'rgba(230,126,34,0.14)',
    },
    resourceThumb: {
      width: 46,
      height: 46,
      borderRadius: 7,
      overflow: 'hidden',
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
    },
    resourceThumbLink: {
      cursor: 'pointer',
    },
    resourceThumbImage: {
      width: '100%',
      height: '100%',
    },
    resourceResultCopy: {
      flex: 1,
      minWidth: 0,
    },
    resourceResultName: {
      fontSize: 12,
      fontWeight: '700',
      color: isDarkMode ? '#ffffff' : '#1a1a1a',
    },
    resourceResultDescription: {
      marginTop: 3,
      fontSize: 10.5,
      lineHeight: 14,
      color: isDarkMode ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.50)',
    },
    resourceSelectMark: {
      width: 18,
      height: 18,
      borderRadius: 9,
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.20)',
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
    },
    resourceSelectMarkSelected: {
      backgroundColor: '#e67e22',
      borderColor: '#e67e22',
    },
    resourceSelectSpinner: {
      transform: [{ scale: 0.55 }],
    },
    resourceEmptyText: {
      paddingVertical: 24,
      textAlign: 'center',
      fontSize: 12,
      color: isDarkMode ? 'rgba(255,255,255,0.42)' : 'rgba(0,0,0,0.42)',
    },
    sideModalBackdrop: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
      backgroundColor: 'rgba(0,0,0,0.62)',
      ...(Platform.OS === 'web' ? { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' } : {}),
    },
    sideModalCard: {
      width: 420,
      maxWidth: '100%',
      maxHeight: '90%',
      padding: 20,
      borderRadius: 12,
      backgroundColor: isDarkMode ? '#171a24' : '#ffffff',
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
      ...(Platform.OS === 'web' ? {
        boxShadow: '0 14px 42px rgba(0,0,0,0.35)',
      } : {}),
    },
    sideModalTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: isDarkMode ? '#ffffff' : '#1a1a1a',
    },
    sideModalSubtitle: {
      marginTop: 5,
      marginBottom: 16,
      fontSize: 12,
      color: isDarkMode ? 'rgba(255,255,255,0.58)' : 'rgba(0,0,0,0.54)',
    },
    sideModalFieldLabel: {
      marginTop: 14,
      marginBottom: 0,
      fontSize: 12,
      fontWeight: '700',
      color: isDarkMode ? 'rgba(255,255,255,0.84)' : 'rgba(0,0,0,0.74)',
    },
    sideModalCheckRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 9,
      paddingVertical: 8,
    },
    sideModalCheckbox: {
      width: 20,
      height: 20,
      borderRadius: 5,
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.24)',
      alignItems: 'center',
      justifyContent: 'center',
    },
    sideModalCheckboxSelected: {
      backgroundColor: '#e67e22',
      borderColor: '#e67e22',
    },
    sideModalCheckLabel: {
      fontSize: 13,
      fontWeight: '600',
      color: isDarkMode ? 'rgba(255,255,255,0.86)' : 'rgba(0,0,0,0.76)',
    },
    sideModalInput: {
      marginTop: 10,
      minHeight: 40,
      paddingVertical: 9,
      paddingHorizontal: 11,
      borderRadius: 7,
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.035)',
      color: isDarkMode ? '#ffffff' : '#1a1a1a',
      fontSize: 12,
      outlineStyle: 'none',
    },
    sideModalInlineFields: {
      flexDirection: 'row',
      gap: 8,
    },
    sideModalInlineInput: {
      flex: 1,
      minWidth: 0,
    },
    sideModalInlineField: {
      flex: 1,
      minWidth: 0,
    },
    sideModalTextArea: {
      minHeight: 76,
      textAlignVertical: 'top',
    },
    sideModalActions: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: 8,
      marginTop: 16,
    },
    sideModalNoButton: {
      paddingVertical: 9,
      paddingHorizontal: 16,
      borderRadius: 6,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
    },
    sideModalNoText: {
      fontSize: 12,
      fontWeight: '700',
      color: isDarkMode ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.62)',
    },
    sideModalYesButton: {
      paddingVertical: 9,
      paddingHorizontal: 16,
      borderRadius: 6,
      backgroundColor: '#e67e22',
    },
    sideModalYesText: {
      fontSize: 12,
      fontWeight: '700',
      color: '#ffffff',
    },
    sideModalButtonDisabled: {
      opacity: 0.38,
    },
    resourceCategoryPicker: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    resourceCategoryOption: {
      width: '48%',
      minHeight: 52,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      padding: 11,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)',
    },
    resourceCategoryOptionText: {
      flex: 1,
      fontSize: 12,
      fontWeight: '700',
      color: isDarkMode ? 'rgba(255,255,255,0.86)' : 'rgba(0,0,0,0.76)',
    },
    resourceCategoryCount: {
      fontSize: 10,
      fontWeight: '600',
      color: isDarkMode ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.44)',
    },
    resourceCategoryImage: {
      width: 28,
      height: 28,
    },
    resourceVenuePickerWrap: {
      position: 'relative',
      zIndex: 4,
      marginBottom: 4,
    },
    resourceVenuePicker: {
      marginTop: 10,
      minHeight: 42,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 11,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.035)',
    },
    resourceVenuePickerSelected: {
      borderColor: 'rgba(0,165,117,0.75)',
    },
    resourceVenuePickerInput: {
      flex: 1,
      minWidth: 0,
      paddingVertical: 9,
      color: isDarkMode ? '#ffffff' : '#1a1a1a',
      fontSize: 12,
      outlineStyle: 'none',
      backgroundColor: 'transparent',
    },
    resourceVenueResults: {
      maxHeight: 190,
      marginTop: 5,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)',
      backgroundColor: isDarkMode ? '#20232f' : '#ffffff',
      ...(Platform.OS === 'web' ? {
        scrollbarWidth: 'thin',
        scrollbarColor: isDarkMode
          ? 'rgba(255,255,255,0.28) transparent'
          : 'rgba(128,128,128,0.4) transparent',
      } : {}),
    },
    resourceVenueResult: {
      paddingVertical: 9,
      paddingHorizontal: 11,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
    },
    resourceVenueResultName: {
      fontSize: 12,
      fontWeight: '700',
      color: isDarkMode ? '#ffffff' : '#1a1a1a',
    },
    resourceVenueResultLocation: {
      marginTop: 2,
      fontSize: 10,
      color: isDarkMode ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.46)',
    },
    resourceVenueEmpty: {
      padding: 12,
      textAlign: 'center',
      fontSize: 11,
      color: isDarkMode ? 'rgba(255,255,255,0.46)' : 'rgba(0,0,0,0.44)',
    },
    resourceScopeRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 7,
      marginTop: 10,
    },
    resourceScopeOption: {
      flexGrow: 1,
      flexBasis: '30%',
      minWidth: 105,
      paddingVertical: 8,
      paddingHorizontal: 9,
      alignItems: 'center',
      borderRadius: 6,
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)',
    },
    resourceScopeOptionActive: {
      backgroundColor: '#4e7de0',
      borderColor: '#4e7de0',
    },
    resourceScopeOptionText: {
      fontSize: 11,
      fontWeight: '700',
      color: isDarkMode ? 'rgba(255,255,255,0.70)' : 'rgba(0,0,0,0.60)',
    },
    resourceScopeOptionTextActive: {
      color: '#ffffff',
    },
    resourceFormContent: {
      paddingBottom: 4,
    },
    resourceFormHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    },
    resourceFormClose: {
      fontSize: 20,
      lineHeight: 22,
      color: isDarkMode ? 'rgba(255,255,255,0.62)' : 'rgba(0,0,0,0.52)',
    },
    resourceFormCategoryBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 11,
      marginTop: 14,
      padding: 11,
      borderRadius: 8,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.035)',
    },
    resourceFormCategoryIcon: {
      width: 30,
      height: 30,
    },
    resourceFormCategoryLabel: {
      fontSize: 10,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: isDarkMode ? 'rgba(255,255,255,0.48)' : 'rgba(0,0,0,0.46)',
    },
    resourceFormCategoryValue: {
      marginTop: 2,
      fontSize: 13,
      fontWeight: '700',
      color: isDarkMode ? '#ffffff' : '#1a1a1a',
    },
    resourceFormHelpText: {
      marginTop: 10,
      fontSize: 11,
      lineHeight: 16,
      color: isDarkMode ? 'rgba(255,255,255,0.56)' : 'rgba(0,0,0,0.54)',
    },
    resourcePhotoDropzone: {
      marginTop: 10,
      paddingVertical: 26,
      alignItems: 'center',
      borderRadius: 8,
      borderWidth: 2,
      borderStyle: 'dashed',
      borderColor: isDarkMode ? 'rgba(255,255,255,0.20)' : 'rgba(0,0,0,0.15)',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.025)',
    },
    resourcePhotoDropzoneTitle: {
      marginTop: 8,
      fontSize: 13,
      fontWeight: '600',
      color: isDarkMode ? '#ffffff' : '#1a1a1a',
    },
    resourcePhotoDropzoneHint: {
      marginTop: 4,
      fontSize: 11,
      color: isDarkMode ? 'rgba(255,255,255,0.52)' : 'rgba(0,0,0,0.48)',
    },
    resourcePhotoPreviewWrap: {
      marginTop: 10,
    },
    resourcePhotoPreview: {
      width: '100%',
      maxHeight: 220,
      borderRadius: 8,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#f5f5f0',
    },
    resourcePhotoPreviewImage: {
      width: '100%',
      height: '100%',
    },
    resourcePhotoActions: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: 8,
    },
    resourcePhotoChangeText: {
      fontSize: 12,
      fontWeight: '700',
      color: '#4e7de0',
    },
    resourcePhotoRemoveText: {
      fontSize: 12,
      fontWeight: '700',
      color: '#d9534f',
    },
    resourceSubmitButton: {
      marginTop: 20,
      paddingVertical: 14,
      alignItems: 'center',
      borderRadius: 8,
      backgroundColor: '#00A575',
    },
    resourceSubmitButtonText: {
      fontSize: 13,
      fontWeight: '700',
      color: '#ffffff',
    },
    resourceFormBackLink: {
      marginTop: 12,
      textAlign: 'center',
      fontSize: 11,
      fontWeight: '700',
      color: isDarkMode ? 'rgba(255,255,255,0.56)' : 'rgba(0,0,0,0.52)',
    },
  });
}
